import React, {
  useState,
  useMemo,
  useEffect,
  useContext,
  useRef,
  useCallback,
} from "react";
import NavBar from "../partials/NavBar";
import { GlobalContext } from "../GlobalContext";
import { parseData } from "../localParser";
import DOMPurify from "dompurify";
import Editor from "@monaco-editor/react";
import MissingTerms from "../partials/MissingTerms";

const KOREAN_RE = /[㄰-㆏가-힣]/;
const isKorean = (t) => KOREAN_RE.test(t || "");
const hasCyrillic = (t) => /[а-яА-ЯёЁ]/.test(t || "");
const KOREAN_BG = "#b7e1cd";
const STORAGE_KEY = "l2wb_state";
const SEL_KEY = "l2wb_sel";
const MARKER_GREENS = new Set(["#b7e1cd", "rgb(183, 225, 205)"]);

// Count English letters, ignoring line breaks, font tags, $sN variables, and the
// stat abbreviations CP/HP/MP (which stay in English even in Russian text).
const englishCount = (s) =>
  ((s || "")
    .replace(/\\n/g, " ")
    .replace(/<\/?font[^>]*>/gi, " ")
    .replace(/\$\w*/g, " ")
    .replace(/\b(?:CP|HP|MP|PvE|PVE|PvP|PVP)\b/gi, " ")
    .match(/[a-zA-Z]/g) || []).length;

// Per-mode language config: source/target for GT, name lookup and term memory.
const MODE_CFG = {
  kr: { gtSl: "ko", gtTl: "en", lookupIn: "KR", lookupOut: "EU", termTarget: "en" },
  ru: { gtSl: "en", gtTl: "ru", lookupIn: "EU", lookupOut: "RU", termTarget: "ru" },
};

const gtLink = (text, mode) => {
  const cfg = MODE_CFG[mode] || MODE_CFG.kr;
  return `https://translate.google.com/?sl=${cfg.gtSl}&tl=${cfg.gtTl}&op=translate&text=${encodeURIComponent(
    text || ""
  )}`;
};

function cleanBg(bg) {
  const low = (bg || "").trim().toLowerCase();
  if (
    !low ||
    low === "transparent" ||
    low === "#ffffff" ||
    low === "white" ||
    low === "rgb(255, 255, 255)" ||
    low === "rgba(0, 0, 0, 0)" ||
    MARKER_GREENS.has(low)
  ) {
    return "";
  }
  return bg;
}
const normalizeBg = (el) => cleanBg((el.style && el.style.backgroundColor) || "");

// Sheet convention: an ORANGE fill marks a row that is completely new (missing
// from the current files). Any orange-ish hue counts - Excel's #FFC000, #ED7D31,
// #F4B084, #F8CBAD, … - so the exact shade does not matter.
function parseColor(bg) {
  const c = (bg || "").trim().toLowerCase();
  let m = c.match(/^#([0-9a-f]{3})$/);
  if (m) return m[1].split("").map((h) => parseInt(h + h, 16));
  m = c.match(/^#([0-9a-f]{6})$/);
  if (m) return [0, 2, 4].map((i) => parseInt(m[1].slice(i, i + 2), 16));
  m = c.match(/^rgba?\(\s*(\d+)\s*,\s*(\d+)\s*,\s*(\d+)/);
  if (m) return [Number(m[1]), Number(m[2]), Number(m[3])];
  if (c === "orange") return [255, 165, 0];
  return null;
}
function isOrangeBg(bg) {
  const rgb = parseColor(bg);
  if (!rgb) return false;
  const [r, g, b] = rgb.map((v) => v / 255);
  const max = Math.max(r, g, b);
  const min = Math.min(r, g, b);
  const d = max - min;
  if (d < 0.08) return false; // grey / white / black
  const l = (max + min) / 2;
  const sat = d / (1 - Math.abs(2 * l - 1));
  let h;
  if (max === r) h = ((g - b) / d) % 6;
  else if (max === g) h = (b - r) / d + 2;
  else h = (r - g) / d + 4;
  h = (h * 60 + 360) % 360;
  // Excel's "Orange" (#FFC000) sits at the yellow end (45°) next to its gold
  // tints (#FFD966, #FFEB9C): past 42° only a strong, mid-lightness fill counts.
  return h >= 14 && h <= 48 && sat >= 0.45 && l >= 0.3 && (h <= 42 ? l <= 0.94 : l <= 0.6);
}

function parseCell(raw) {
  const eq = raw.indexOf("=");
  if (eq === -1) return { key: null, value: raw, wrapped: false, isMarker: true };
  const key = raw.substring(0, eq);
  let v = raw.substring(eq + 1);
  let wrapped = false;
  if (v.length >= 2 && v.startsWith("[") && v.endsWith("]")) {
    wrapped = true;
    v = v.slice(1, -1);
  }
  return { key, value: v, wrapped, isMarker: false };
}
const makeCell = (raw, bg) => {
  // DAT line breaks are the literal two chars "\n"; any REAL newline in a pasted
  // cell is a clipboard-HTML formatting artifact (newline + indentation), so
  // collapse those (and non-breaking spaces) to a single space. Keeps cells one-line.
  const clean = (raw || "")
    .replace(/ /g, " ")
    .replace(/[ \t]*\r?\n[ \t]*/g, " ")
    .trim();
  const cell = { raw: clean, bg: bg || "", ...parseCell(clean) };
  // The pristine text as pasted. Edits (cellWithValue) keep it; "occurrences"
  // are matched on it, so a saved half-finished edit still belongs to its group.
  cell.orig = cell.value;
  return cell;
};
function cellWithValue(cell, value) {
  const v = value == null ? "" : String(value);
  const raw = cell.isMarker ? v : `${cell.key}=${cell.wrapped ? `[${v}]` : v}`;
  return { ...cell, value: v, raw };
}

function parseSheetHtml(html) {
  const doc = new DOMParser().parseFromString(html, "text/html");
  const table = doc.querySelector("table");
  if (!table) return [];
  const rows = [];
  table.querySelectorAll("tr").forEach((tr) => {
    const cells = [];
    tr.querySelectorAll("td,th").forEach((td) => {
      cells.push({ text: (td.textContent || "").replace(/ /g, " "), bg: normalizeBg(td) });
    });
    if (cells.length) rows.push(cells);
  });
  return rows;
}
function parseTsv(text) {
  return text
    .replace(/\r\n/g, "\n")
    .split("\n")
    .filter((line, i, arr) => !(i === arr.length - 1 && line === ""))
    .map((line) => line.split("\t").map((t) => ({ text: t, bg: "" })));
}
function normalizeGrid(rows) {
  const cols = rows.reduce((m, r) => Math.max(m, r.length), 0);
  return rows.map((r) => {
    const out = r.slice();
    while (out.length < cols) out.push(makeCell("", ""));
    return out;
  });
}

// A padding cell inserted only to align columns for display; stripped on copy so
// the raw file structure is preserved exactly.
const synthCell = () => ({ ...makeCell("", ""), synthetic: true });

// Column sets that together identify a row. If a pasted grid contains every
// column of a set, the whole set becomes the key (skills: id + level + sublevel).
const KEY_SETS = [["skill_id", "skill_level", "skill_sublevel"]];

// Index reference records by the composite of `keyFields` — the same way grid
// rows are keyed — or by the parser's own `key` (item_id|RequiredEnchant) for
// enchanted skills. Keyed this way every skill level gets its own entry instead
// of all levels collapsing onto skill_id.
function indexRefRecords(ref, keyFields, enchanted) {
  const m = new Map();
  if (!ref || !Array.isArray(ref.records)) return m;
  for (const r of ref.records) {
    let key;
    if (enchanted) {
      key = r.key;
    } else if (keyFields.length) {
      key = keyFields
        .map((f) => {
          const v = r.fields ? r.fields[f] : undefined;
          return String(v == null ? "" : v).trim();
        })
        .join("|");
    } else {
      key = String(r.id == null ? "" : r.id).trim();
    }
    if (key != null && key !== "" && !m.has(key)) m.set(key, r);
  }
  return m;
}

// Enchanted-item-skill rows are misaligned: the begin line carries
// `enchanted_item_skill_begin` + `item_id` before its desc block, while sub rows
// have only a leading tab. Prepend synthetic cells to each sub desc row so every
// desc block (RequiredEnchant / text / …) lands in the same column.
function alignEnchantedGrid(rows) {
  const descStart = (row) =>
    row.findIndex((c) => (c.isMarker && c.raw === "desc_begin") || c.key === "RequiredEnchant");
  const beginRow = rows.find((r) =>
    r.some((c) => c.isMarker && c.raw === "enchanted_item_skill_begin")
  );
  const target = beginRow ? descStart(beginRow) : 2;
  if (target < 0) return normalizeGrid(rows);

  const shifted = rows.map((row) => {
    const isBegin = row.some((c) => c.isMarker && c.raw === "enchanted_item_skill_begin");
    const isEnd = row.some((c) => c.isMarker && c.raw === "enchanted_item_skill_end");
    if (isBegin || isEnd) return row.slice();
    const ds = descStart(row);
    if (ds < 0) return row.slice();
    const need = target - ds;
    if (need <= 0) return row.slice();
    return [...Array.from({ length: need }, synthCell), ...row];
  });

  const cols = shifted.reduce((m, r) => Math.max(m, r.length), 0);
  return shifted.map((r) => {
    const out = r.slice();
    while (out.length < cols) out.push(synthCell());
    return out;
  });
}

// Word-level diff. Tags (<...>) are atomic tokens so highlighting/escaping never
// splits them, and whitespace is kept as tokens so the text reconstructs exactly.
const tokenize = (s) => (s || "").match(/<[^>]*>|[^\s<]+|\s+|</g) || [];
function diffTokens(aStr, bStr) {
  const a = tokenize(aStr);
  const b = tokenize(bStr);
  const n = a.length;
  const m = b.length;
  const dp = Array.from({ length: n + 1 }, () => new Array(m + 1).fill(0));
  for (let i = n - 1; i >= 0; i--) {
    for (let j = m - 1; j >= 0; j--) {
      dp[i][j] = a[i] === b[j] ? dp[i + 1][j + 1] + 1 : Math.max(dp[i + 1][j], dp[i][j + 1]);
    }
  }
  const out = [];
  let i = 0;
  let j = 0;
  while (i < n && j < m) {
    if (a[i] === b[j]) {
      out.push({ t: a[i], type: "same" });
      i++;
      j++;
    } else if (dp[i + 1][j] >= dp[i][j + 1]) {
      out.push({ t: a[i], type: "del" });
      i++;
    } else {
      out.push({ t: b[j], type: "add" });
      j++;
    }
  }
  while (i < n) out.push({ t: a[i++], type: "del" });
  while (j < m) out.push({ t: b[j++], type: "add" });
  return out;
}

// Render one side of a diff as pretty L2 HTML: font colors -> colored spans,
// newlines -> <br>, and changed tokens wrapped in a highlight span. Sentinels
// keep our markup safe through the escape step; DOMPurify sanitizes the result.
const S_OPEN = String.fromCharCode(1);
const S_CLOSE = String.fromCharCode(2);
const T_LT = String.fromCharCode(3);
const T_GT = String.fromCharCode(4);
function prettyDiff(segs, which) {
  const highlightType = which === "before" ? "del" : "add";
  const exclude = which === "before" ? "add" : "del";
  let src = segs
    .filter((s) => s.type !== exclude)
    .map((s) => (s.type === highlightType ? S_OPEN + s.t + S_CLOSE : s.t))
    .join("");
  src = src.replace(
    /<font color=('|")?([^"'>\s]+)\1?>([\s\S]*?)<\/font>/gi,
    (m, q, color, content) => `${T_LT}span style="color:#${color}"${T_GT}${content}${T_LT}/span${T_GT}`
  );
  src = src.replace(/</g, "&lt;").replace(/>/g, "&gt;");
  src = src.split(T_LT).join("<").split(T_GT).join(">");
  src = src.replace(/\n/g, "<br>");
  const cls = which === "before" ? "wb-del" : "wb-add";
  src = src.split(S_OPEN).join(`<span class="${cls}">`).split(S_CLOSE).join("</span>");
  return DOMPurify.sanitize(src, { ALLOWED_TAGS: ["span", "br"], ALLOWED_ATTR: ["style", "class"] });
}

// Count line breaks (literal "\n" as used in the data, plus any real newlines).
const countNewlines = (s) =>
  ((s || "").match(/\\n/g) || []).length + ((s || "").match(/\n/g) || []).length;

// Count opening <font ...> tags.
const countFontTags = (s) => ((s || "").match(/<font\b/gi) || []).length;

// Extract quantity/level numbers. Only <font ...> tags are stripped (to drop the
// color hex); other angle-bracket text like "<+6 이상 ...>" is kept, since those
// numbers are real content. $sN placeholders are ignored too.
function extractNumbers(s) {
  const cleaned = (s || "").replace(/<\/?font[^>]*>/gi, " ").replace(/\$\w*/g, " ");
  return (cleaned.match(/\d[\d,]*(?:\.\d+)?/g) || []).map((n) => n.replace(/,/g, ""));
}

// Multiset difference: what's in `a` but not `b`, and vice-versa.
function multisetDiff(a, b) {
  const count = {};
  b.forEach((n) => (count[n] = (count[n] || 0) + 1));
  const extra = [];
  a.forEach((n) => {
    if (count[n] > 0) count[n] -= 1;
    else extra.push(n);
  });
  const missing = [];
  Object.keys(count).forEach((n) => {
    for (let i = 0; i < count[n]; i++) missing.push(n);
  });
  return { extra, missing };
}

// ---- pretty preview: game-like rendering of a cell and its reference ----
// Split a DAT string into display lines: the literal two-char "\n" the data
// uses, plus any real newline.
const splitDatLines = (s) => (s || "").replace(/\r\n/g, "\n").split(/\\n|\n/);

// A line's structural shape - blank?, font tags, numbers - which is what pairs a
// cell line with a reference line when the two texts are in different languages.
const lineShape = (line) =>
  JSON.stringify([!line.trim(), countFontTags(line), extractNumbers(line).slice().sort()]);

// Longest common subsequence of two shape lists -> [i, j] index pairs.
function lcsPairs(a, b) {
  const n = a.length;
  const m = b.length;
  const dp = Array.from({ length: n + 1 }, () => new Array(m + 1).fill(0));
  for (let i = n - 1; i >= 0; i--) {
    for (let j = m - 1; j >= 0; j--) {
      dp[i][j] = a[i] === b[j] ? dp[i + 1][j + 1] + 1 : Math.max(dp[i + 1][j], dp[i][j + 1]);
    }
  }
  const pairs = [];
  let i = 0;
  let j = 0;
  while (i < n && j < m) {
    if (a[i] === b[j]) {
      pairs.push([i, j]);
      i++;
      j++;
    } else if (dp[i + 1][j + 1] === dp[i][j]) {
      // Both lines are unmatched and skipping both costs nothing: do that, so
      // a changed line pairs with ITS counterpart instead of a later look-alike.
      i++;
      j++;
    } else if (dp[i + 1][j] >= dp[i][j + 1]) i++;
    else j++;
  }
  return pairs;
}

// The lines the ⚠ mismatch chips are about: every line with no structural
// counterpart on the other side (an extra / missing line, a changed number, a
// font tag more or less). Returns index sets for the cell and the reference.
function mismatchedLines(cellText, refText) {
  const a = splitDatLines(cellText).map(lineShape);
  const b = splitDatLines(refText).map(lineShape);
  const cell = new Set(a.map((_, i) => i));
  const ref = new Set(b.map((_, j) => j));
  for (const [i, j] of lcsPairs(a, b)) {
    cell.delete(i);
    ref.delete(j);
  }
  return { cell, ref };
}

// Render a DAT string the way the game shows it: font colors as colored text
// (a color carries across lines until its </font>), "\n" as line breaks, "%%"
// as "%". One block per line so mismatched lines can be highlighted. Sentinels
// keep our markup safe through the escape step; DOMPurify sanitizes the result.
function prettyPreview(text, badLines) {
  const stack = []; // open font colors, carried across lines
  const spanOpen = (c) => `${T_LT}span style="color:${c}"${T_GT}`;
  const spanClose = `${T_LT}/span${T_GT}`;
  const cssColor = (raw) => {
    const c = (raw || "").replace(/^#/, "");
    if (/^[0-9a-f]{6}$/i.test(c) || /^[0-9a-f]{3}$/i.test(c)) return `#${c}`;
    if (/^[a-z]+$/i.test(c)) return c.toLowerCase();
    return "inherit";
  };
  const re = /<font\s+color=('|")?([^"'>\s]+)\1?\s*>|<\/font\s*>/gi;
  const html = splitDatLines(text)
    .map((line, i) => {
      let out = stack.map(spanOpen).join("");
      let last = 0;
      let m;
      re.lastIndex = 0;
      while ((m = re.exec(line))) {
        out += line.slice(last, m.index);
        if (m[0][1] === "/") {
          if (stack.length) {
            stack.pop();
            out += spanClose;
          }
        } else {
          const c = cssColor(m[2]);
          stack.push(c);
          out += spanOpen(c);
        }
        last = m.index + m[0].length;
      }
      out += line.slice(last);
      out += stack.map(() => spanClose).join("");
      out = out
        .replace(/</g, "&lt;")
        .replace(/>/g, "&gt;")
        .split(T_LT)
        .join("<")
        .split(T_GT)
        .join(">")
        .replace(/%%/g, "%");
      const cls = badLines && badLines.has(i) ? "wb-pv-line wb-pv-bad" : "wb-pv-line";
      return `<div class="${cls}">${out || "&nbsp;"}</div>`;
    })
    .join("");
  return DOMPurify.sanitize(html, { ALLOWED_TAGS: ["div", "span"], ALLOWED_ATTR: ["style", "class"] });
}

// ---- find & replace engine: one code path for the preview and for the apply
// step, so what the preview shows is exactly what gets written ----
const escapeFindRx = (s) => s.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");

// Build the search regex. Plain text is escaped; regex mode takes JS syntax.
function buildFinder(find, useRegex, matchCase) {
  if (!find) return { re: null, error: null };
  try {
    return { re: new RegExp(useRegex ? find : escapeFindRx(find), matchCase ? "g" : "gi"), error: null };
  } catch (e) {
    return { re: null, error: e.message };
  }
}

// JS replacement patterns: $$, $&, $`, $', $1..$99, $<name>. ("$s1" is not one,
// so L2 variables in a replacement stay as they are.)
function expandReplacement(template, match, groups, offset, str, named) {
  return template.replace(/\$(\$|&|`|'|\d{1,2}|<[^>]*>)/g, (tok, k) => {
    if (k === "$") return "$";
    if (k === "&") return match;
    if (k === "`") return str.slice(0, offset);
    if (k === "'") return str.slice(offset + match.length);
    if (k[0] === "<") {
      if (!named) return tok;
      const v = named[k.slice(1, -1)];
      return v == null ? "" : v;
    }
    // $n / $nn: a two-digit number that is not a group falls back to one digit.
    let n = Number(k);
    if (n >= 1 && n <= groups.length) return groups[n - 1] == null ? "" : groups[n - 1];
    if (k.length === 2) {
      n = Number(k[0]);
      if (n >= 1 && n <= groups.length) return (groups[n - 1] == null ? "" : groups[n - 1]) + k[1];
    }
    return tok;
  });
}

// Replace every match in `text`. Returns the new text plus the hits
// [{start, end, match, repl}] (positions in the ORIGINAL text).
function replaceInText(text, re, template, useRegex) {
  const hits = [];
  re.lastIndex = 0;
  const out = text.replace(re, (...args) => {
    const last = args[args.length - 1];
    const hasNamed = typeof last === "object" && last !== null;
    const tail = hasNamed ? 3 : 2; // [offset, string] (+ named groups)
    const offset = args[args.length - tail];
    const str = args[args.length - tail + 1];
    const match = args[0];
    const groups = args.slice(1, args.length - tail);
    const repl = useRegex
      ? expandReplacement(template, match, groups, offset, str, hasNamed ? last : null)
      : template;
    hits.push({ start: offset, end: offset + match.length, match, repl });
    return repl;
  });
  return { out, hits };
}

// Split a replaced cell into display segments: the original text with its
// matches marked, and the result with the replacements marked.
function replaceSegments(text, hits) {
  const before = [];
  const after = [];
  let at = 0;
  for (const h of hits) {
    if (h.start > at) {
      const plain = text.slice(at, h.start);
      before.push({ t: plain });
      after.push({ t: plain });
    }
    before.push({ t: h.match, hit: true });
    after.push({ t: h.repl, hit: true });
    at = h.end;
  }
  if (at < text.length) {
    before.push({ t: text.slice(at) });
    after.push({ t: text.slice(at) });
  }
  return { before, after };
}

// ---- character diff (old client vs new client) ----
// Units are single characters, except that a tag ("<font color=...>") stays whole
// so a changed colour code shows as one changed tag. Runs are merged. Returns
// [{t, type: "same" | "del" | "add"}]; falls back to the word diff on huge inputs.
function diffChars(aStr, bStr) {
  const a = (aStr || "").match(/<[^>]*>|[\s\S]/gu) || [];
  const b = (bStr || "").match(/<[^>]*>|[\s\S]/gu) || [];
  const n = a.length;
  const m = b.length;
  if (n * m > 4000000) return diffTokens(aStr, bStr);
  const dp = Array.from({ length: n + 1 }, () => new Int32Array(m + 1));
  for (let i = n - 1; i >= 0; i--) {
    for (let j = m - 1; j >= 0; j--) {
      dp[i][j] = a[i] === b[j] ? dp[i + 1][j + 1] + 1 : Math.max(dp[i + 1][j], dp[i][j + 1]);
    }
  }
  const out = [];
  const push = (type, t) => {
    const last = out[out.length - 1];
    if (last && last.type === type) last.t += t;
    else out.push({ t, type });
  };
  let i = 0;
  let j = 0;
  while (i < n && j < m) {
    if (a[i] === b[j]) {
      push("same", a[i]);
      i++;
      j++;
    } else if (dp[i + 1][j] >= dp[i][j + 1]) {
      push("del", a[i]);
      i++;
    } else {
      push("add", b[j]);
      j++;
    }
  }
  while (i < n) push("del", a[i++]);
  while (j < m) push("add", b[j++]);
  return out;
}

// Render one side of a character diff the way the preview renders text: font
// colours as coloured text, line breaks as real lines, changed runs highlighted.
// A font tag that itself changed (a new colour code) is shown literally inside
// the highlight, since that is a real difference; unchanged tags are consumed.
// Every run is one self-contained span (colour + highlight), so there is no
// nesting that a change boundary could break. DOMPurify sanitises the result.
const fontCssColor = (raw) => {
  const c = (raw || "").replace(/^#/, "");
  if (/^[0-9a-f]{6}$/i.test(c) || /^[0-9a-f]{3}$/i.test(c)) return `#${c}`;
  if (/^[a-z]+$/i.test(c)) return c.toLowerCase();
  return "inherit";
};
function prettyDiffHtml(segs, which) {
  const hl = which === "before" ? "del" : "add";
  const skip = which === "before" ? "add" : "del";
  const esc = (s) => s.replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;");
  const stack = [];
  const out = [];
  let buf = "";
  const flush = (changed) => {
    if (!buf) return;
    const color = stack.length ? stack[stack.length - 1] : null;
    const cls = changed ? ` class="wb-${hl}"` : "";
    const style = color ? ` style="color:${color}"` : "";
    out.push(`<span${cls}${style}>${esc(buf).replace(/\n/g, "<br>")}</span>`);
    buf = "";
  };
  for (const seg of segs) {
    if (seg.type === skip) continue;
    const changed = seg.type === hl;
    for (const u of seg.t.match(/<[^>]*>|[\s\S]/gu) || []) {
      const open = u.match(/^<font\s+color=('|")?([^"'>\s]+)\1?\s*>$/i);
      const close = /^<\/font\s*>$/i.test(u);
      if (open || close) {
        flush(changed);
        if (changed) out.push(`<span class="wb-${hl} wb-tagchange">${esc(u)}</span>`);
        if (open) stack.push(fontCssColor(open[2]));
        else if (stack.length) stack.pop();
      } else {
        buf += u;
      }
    }
    flush(changed);
  }
  return DOMPurify.sanitize(out.join(""), { ALLOWED_TAGS: ["span", "br"], ALLOWED_ATTR: ["style", "class"] });
}

const Workbench = () => {
  const { username, logout, updateDatabase, toast, socket, isSocketReady, versions } =
    useContext(GlobalContext);

  const [grid, setGrid] = useState([]);
  const [english, setEnglish] = useState(null);
  const [englishName, setEnglishName] = useState("");
  // Optional second (non-English) reference: RU in EN→RU mode, KR in KR→EN mode.
  // Lets unchanged rows auto-fill from the existing translation when the pasted
  // SOURCE text matches the source-language reference.
  const [altRef, setAltRef] = useState(null);
  const [altRefName, setAltRefName] = useState("");
  const [altDragOver, setAltDragOver] = useState(false);
  // Third reference: the OLD client of the language being translated FROM (KR in
  // KR->EN, EN in EN->RU), to see what changed between clients for a field.
  const [oldRef, setOldRef] = useState(null);
  const [oldRefName, setOldRefName] = useState("");
  const [oldDragOver, setOldDragOver] = useState(false);
  // Key columns: the column(s) whose values identify a row. Items use one (`id`);
  // skills need three (skill_id + skill_level + skill_sublevel). Composite = join("|").
  const [keyCols, setKeyCols] = useState([0]);
  const [keyColsManual, setKeyColsManual] = useState(false);
  // Auto-fill matching: "field" fills each unchanged translatable cell on its own;
  // "row" only fills a row when EVERY translatable cell is unchanged.
  const [fillMode, setFillMode] = useState("field");

  // ---- row virtualization (only render the rows in view; grids can be 16k+ rows) ----
  const [view, setView] = useState({ top: 0, h: 600 });
  const [rowH, setRowH] = useState(68);
  const rowHRef = useRef(0);
  const rafRef = useRef(0);
  const visRowIdxRef = useRef([]);
  const onGridScroll = useCallback(() => {
    if (rafRef.current) return;
    rafRef.current = requestAnimationFrame(() => {
      rafRef.current = 0;
      const el = tableRef.current;
      if (el) setView({ top: el.scrollTop, h: el.clientHeight });
    });
  }, []);
  // Measure the real row height once so the spacer padding matches exactly.
  const measureRow = useCallback((el) => {
    if (el && !rowHRef.current) {
      const h = el.getBoundingClientRect().height;
      if (h > 0) {
        rowHRef.current = h;
        setRowH(h);
      }
    }
  }, []);

  const [selected, setSelected] = useState(null);
  const [popover, setPopover] = useState(false);
  const [editValue, setEditValue] = useState("");
  const [gt, setGt] = useState({ status: "none" });
  const [tm, setTm] = useState({ status: "idle" });

  const [mode, setMode] = useState("kr"); // "kr" (KR->EN) or "ru" (EN->RU)
  const [autoGt, setAutoGt] = useState(true); // auto-run Google Translate on cell open
  const autoGtRef = useRef(true);
  useEffect(() => {
    autoGtRef.current = autoGt;
  }, [autoGt]);
  const [hideUnstyled, setHideUnstyled] = useState(false);
  const [rowFilter, setRowFilter] = useState("all"); // all | new | newInRefs | newNotInRefs | diffRef
  // "Differs from the target reference" mode: the columns compared
  // ([] = every text column except the key columns).
  const [diffCols, setDiffCols] = useState([]);
  // Game-like preview column (cell + reference) to the right of the sidebar.
  const [showPreview, setShowPreview] = useState(() => {
    try {
      return localStorage.getItem("l2wb_preview") === "1";
    } catch (e) {
      return false;
    }
  });
  useEffect(() => {
    try {
      localStorage.setItem("l2wb_preview", showPreview ? "1" : "0");
    } catch (e) {
      /* storage unavailable */
    }
  }, [showPreview]);
  const [ignoredKeys, setIgnoredKeys] = useState(() => new Set());
  const [find, setFind] = useState("");
  const [replace, setReplace] = useState("");
  const [matchCase, setMatchCase] = useState(false);
  const [useRegex, setUseRegex] = useState(false);
  const [frPreview, setFrPreview] = useState(null); // {cells, matches} while the replace preview is open
  // Find & replace lives in a floating panel on the left; remember if it is open.
  const [frOpen, setFrOpen] = useState(() => {
    try {
      return localStorage.getItem("l2wb_fr_open") === "1";
    } catch (e) {
      return false;
    }
  });
  useEffect(() => {
    try {
      localStorage.setItem("l2wb_fr_open", frOpen ? "1" : "0");
    } catch (e) {
      /* storage unavailable */
    }
  }, [frOpen]);
  // Cell editor: "monaco" (VS Code-like, the default) or "plain" (the old text box).
  const [editorKind, setEditorKind] = useState(() => {
    try {
      return localStorage.getItem("l2wb_editor") === "plain" ? "plain" : "monaco";
    } catch (e) {
      return "monaco";
    }
  });
  useEffect(() => {
    try {
      localStorage.setItem("l2wb_editor", editorKind);
    } catch (e) {
      /* storage unavailable */
    }
  }, [editorKind]);
  const monacoRef = useRef(null); // the live editor instance (null while unmounted)
  const monacoBoxRef = useRef(null); // its box: the height follows the content
  const nameLookupRef = useRef(null);
  const [lastSaved, setLastSaved] = useState(null);
  const [engDragOver, setEngDragOver] = useState(false);
  const [ctxMenu, setCtxMenu] = useState(null); // right-click name-lookup menu

  const tableRef = useRef(null);
  const gtCache = useRef(new Map());
  const restored = useRef(false);
  const gridRef = useRef([]);
  const selectedRef = useRef(null);
  const editValueRef = useRef("");
  const history = useRef({ past: [], future: [] });
  const pendingSelectRef = useRef(null);
  const gtReqId = useRef(0);
  const tmReqId = useRef(0);
  const lookupIdRef = useRef(0);
  const wbToastId = useRef(null);

  // Workbench toasts: bottom-right, brief, and each new one replaces the previous
  // so rapid actions don't pile up a stack that covers the panel.
  const wbToast = useCallback(
    (type, msg) => {
      if (!toast) return;
      if (wbToastId.current != null) toast.dismiss(wbToastId.current);
      const fn = toast[type] || toast;
      wbToastId.current = fn(msg, {
        position: "bottom-right",
        autoClose: 1200,
        hideProgressBar: true,
      });
    },
    [toast]
  );
  const refValueRef = useRef(null);
  const selWasGreenRef = useRef(false);

  useEffect(() => {
    gridRef.current = grid;
  }, [grid]);
  useEffect(() => {
    selectedRef.current = selected;
  }, [selected]);
  useEffect(() => {
    editValueRef.current = editValue;
  }, [editValue]);

  const maxCols = useMemo(() => grid.reduce((m, r) => Math.max(m, r.length), 0), [grid]);
  const columnKeys = useMemo(() => {
    const keys = new Array(maxCols).fill(null);
    for (let c = 0; c < maxCols; c++) {
      for (const row of grid) {
        const cell = row[c];
        if (cell && !cell.isMarker && cell.key) {
          keys[c] = cell.key;
          break;
        }
      }
    }
    return keys;
  }, [grid, maxCols]);
  const visCols = useMemo(
    () => columnKeys.map((k, c) => (k !== null ? c : -1)).filter((c) => c >= 0),
    [columnKeys]
  );

  // Enchanted-item-skill data is nested: one item_id block holds several
  // `desc_begin` rows (one per RequiredEnchant), and item_id appears only on the
  // first. Detect it and build a per-row reference key of `item_id|RequiredEnchant`,
  // propagating item_id down the block.
  const isEnchanted = useMemo(
    () => grid.some((row) => row.some((c) => c.isMarker && c.raw === "enchanted_item_skill_begin")),
    [grid]
  );
  const rowKeys = useMemo(() => {
    if (!isEnchanted) return [];
    const keys = new Array(grid.length).fill(null);
    let curItem = "";
    for (let r = 0; r < grid.length; r++) {
      const row = grid[r];
      const idCell = row.find((c) => c.key === "item_id");
      if (idCell) curItem = (idCell.value || "").trim();
      const reqCell = row.find((c) => c.key === "RequiredEnchant");
      keys[r] = reqCell ? `${curItem}|${(reqCell.value || "").trim()}` : null;
    }
    return keys;
  }, [grid, isEnchanted]);

  // The key used to look up a row's equivalent record in the uploaded reference.
  const rowLookupKey = useCallback(
    (r) => {
      if (isEnchanted) return rowKeys[r] || "";
      const g = gridRef.current[r] || [];
      return keyCols.map((c) => ((g[c] && g[c].value) || "").trim()).join("|");
    },
    [isEnchanted, rowKeys, keyCols]
  );

  // Field names of the key columns (e.g. ["skill_id","skill_level","skill_sublevel"]).
  const keyFields = useMemo(
    () => keyCols.map((c) => columnKeys[c]).filter((k) => k != null),
    [keyCols, columnKeys]
  );
  // Reference records indexed by the same composite key the grid rows use, so a
  // skill level resolves to ITS OWN record instead of all levels collapsing on skill_id.
  const englishIndex = useMemo(
    () => indexRefRecords(english, keyFields, isEnchanted),
    [english, keyFields, isEnchanted]
  );
  const altIndex = useMemo(
    () => indexRefRecords(altRef, keyFields, isEnchanted),
    [altRef, keyFields, isEnchanted]
  );
  const oldIndex = useMemo(
    () => indexRefRecords(oldRef, keyFields, isEnchanted),
    [oldRef, keyFields, isEnchanted]
  );

  const isStringCell = useCallback(
    (cell) => {
      if (cell.isMarker || !cell.wrapped) return false;
      if (cell.key && ignoredKeys.has(cell.key)) return false;
      const v = (cell.value || "").trim().toLowerCase();
      return v !== "" && v !== "none";
    },
    [ignoredKeys]
  );

  // Does this string cell still need work?
  //  - KR mode: it still contains Korean.
  //  - RU mode: it has more than 5 English letters left to translate.
  const cellNeedsWork = useCallback(
    (cell, val) => {
      if (!isStringCell(cell)) return false;
      const v = val === undefined ? cell.value : val;
      return mode === "ru" ? englishCount(v) > 5 : isKorean(v);
    },
    [mode, isStringCell]
  );

  const effectiveBg = useCallback(
    (cell, val) => {
      const v = val === undefined ? cell.value : val;
      if (cell.key && ignoredKeys.has(cell.key)) return cell.bg || "";
      return cellNeedsWork(cell, v) ? KOREAN_BG : cell.bg || "";
    },
    [ignoredKeys, cellNeedsWork]
  );

  // Row still needs work (drives the hide filter, progress and navigation).
  //  - KR mode: any string cell still contains Korean.
  //  - RU mode: no Russian anywhere in the string cells, OR >5 English letters
  //    remain across them (background colors are ignored).
  const rowUnfinished = useCallback(
    (row) => {
      const cells = row.filter(isStringCell);
      if (!cells.length) return false;
      if (mode === "ru") {
        const anyRussian = cells.some((c) => hasCyrillic(c.value));
        const totalEnglish = cells.reduce((n, c) => n + englishCount(c.value), 0);
        return !anyRussian || totalEnglish > 5;
      }
      return cells.some((c) => isKorean(c.value));
    },
    [mode, isStringCell]
  );

  // Progress: unfinished rows vs total.
  const progress = useMemo(() => {
    let green = 0;
    for (const row of grid) if (rowUnfinished(row)) green += 1;
    return { green, total: grid.length, done: grid.length - green };
  }, [grid, rowUnfinished]);

  const copyText = useCallback(
    (text) => {
      if (text == null) return;
      navigator.clipboard.writeText(String(text));
      wbToast("success", "Copied");
    },
    [wbToast]
  );
  // "New" row: the sheet's orange fill (see isOrangeBg) on any of its cells.
  const rowIsNew = useCallback(
    (row) => row.some((c) => !c.synthetic && isOrangeBg(c.bg)),
    []
  );
  // Row present in BOTH reference files, by the same key the auto-fill uses.
  const inBothRefs = useCallback(
    (r) => {
      const key = rowLookupKey(r);
      return !!key && englishIndex.has(key) && altIndex.has(key);
    },
    [rowLookupKey, englishIndex, altIndex]
  );
  const refsReady = englishIndex.size > 0 && altIndex.size > 0;

  // The TARGET-language reference (EN in KR->EN, RU in EN->RU) and the columns
  // the "differs from reference" mode compares: text columns (cells written as
  // [...]) minus the key columns, unless chosen explicitly.
  const targetIdx = mode === "ru" ? altIndex : englishIndex;
  const targetLabel = mode === "ru" ? "RU" : "EN";
  const textCols = useMemo(
    () => visCols.filter((c) => columnKeys[c] && grid.some((row) => row[c] && row[c].wrapped)),
    [visCols, columnKeys, grid]
  );
  const diffColsEff = useMemo(() => {
    const chosen = diffCols.filter((c) => textCols.includes(c));
    return chosen.length ? chosen : textCols.filter((c) => !keyCols.includes(c));
  }, [diffCols, textCols, keyCols]);
  // A row whose compared columns differ from the target reference. Rows the
  // reference does not know cannot differ.
  const rowDiffersFromRef = useCallback(
    (r) => {
      const key = rowLookupKey(r);
      const rec = key ? targetIdx.get(key) : null;
      if (!rec || !rec.fields) return false;
      const row = gridRef.current[r] || [];
      for (const c of diffColsEff) {
        const k = columnKeys[c];
        const rv = k ? rec.fields[k] : undefined;
        if (rv === undefined) continue;
        const cell = row[c];
        if (!cell || cell.isMarker) continue;
        if ((cell.value || "") !== String(rv)) return true;
      }
      return false;
    },
    [rowLookupKey, targetIdx, diffColsEff, columnKeys]
  );

  const filterCounts = useMemo(() => {
    let fresh = 0;
    let both = 0;
    let diff = 0;
    for (let r = 0; r < grid.length; r++) {
      if (targetIdx.size && rowDiffersFromRef(r)) diff += 1;
      if (!rowIsNew(grid[r])) continue;
      fresh += 1;
      if (inBothRefs(r)) both += 1;
    }
    return { fresh, both, diff };
  }, [grid, rowIsNew, inBothRefs, targetIdx, rowDiffersFromRef]);

  // Which rows are shown: the "hide finished" toggle AND the "Show" filter.
  const rowVisible = useCallback(
    (row, r) => {
      if (hideUnstyled && !rowUnfinished(row)) return false;
      if (rowFilter === "all") return true;
      if (rowFilter === "diffRef") return targetIdx.size > 0 && rowDiffersFromRef(r);
      if (!rowIsNew(row)) return false;
      if (rowFilter === "new" || !refsReady) return true;
      const both = inBothRefs(r);
      return rowFilter === "newInRefs" ? both : !both;
    },
    [hideUnstyled, rowUnfinished, rowFilter, rowIsNew, refsReady, inBothRefs, targetIdx, rowDiffersFromRef]
  );

  // Indices of the rows currently shown (respects the hide filter). The grid
  // renders only a scroll window of these.
  const visRowIdx = useMemo(() => {
    const idx = [];
    for (let r = 0; r < grid.length; r++) if (rowVisible(grid[r], r)) idx.push(r);
    return idx;
  }, [grid, rowVisible]);
  useEffect(() => {
    visRowIdxRef.current = visRowIdx;
  }, [visRowIdx]);
  // Re-measure the container whenever the visible set changes so the initial
  // (pre-scroll) window fills tall screens.
  useEffect(() => {
    const el = tableRef.current;
    if (el) setView({ top: el.scrollTop, h: el.clientHeight });
  }, [visRowIdx]);

  // First visible row strictly after / before `fromR` (works even when the row
  // at `fromR` is itself hidden — e.g. it just got resolved).
  const nextVisibleRow = useCallback(
    (fromR) => {
      const g = gridRef.current;
      for (let i = fromR + 1; i < g.length; i++) if (rowVisible(g[i], i)) return i;
      return -1;
    },
    [rowVisible]
  );
  const prevVisibleRow = useCallback(
    (fromR) => {
      const g = gridRef.current;
      for (let i = fromR - 1; i >= 0; i--) if (rowVisible(g[i], i)) return i;
      return -1;
    },
    [rowVisible]
  );

  // The next "needs work" (green) cell after (fromR, fromC) in row-major order
  // over the visible columns, wrapping to the first if none follow.
  const nextGreenCell = useCallback(
    (fromR, fromC) => {
      const g = gridRef.current;
      const greens = [];
      for (let r = 0; r < g.length; r++) {
        for (const c of visCols) if (g[r][c] && cellNeedsWork(g[r][c])) greens.push({ r, c });
      }
      if (!greens.length) return null;
      return greens.find((p) => p.r > fromR || (p.r === fromR && p.c > fromC)) || greens[0];
    },
    [cellNeedsWork, visCols]
  );

  const toggleIgnore = (key) => {
    if (!key) return;
    setIgnoredKeys((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  };

  // ---- history-aware grid mutation ----
  const mutateGrid = useCallback((fn) => {
    const prev = gridRef.current;
    const next = fn(prev);
    if (next === prev) return;
    history.current.past.push(prev);
    if (history.current.past.length > 200) history.current.past.shift();
    history.current.future = [];
    gridRef.current = next;
    setGrid(next);
  }, []);

  const undo = useCallback(() => {
    const h = history.current;
    if (!h.past.length) return;
    const prev = h.past.pop();
    h.future.push(gridRef.current);
    gridRef.current = prev;
    setGrid(prev);
    const sel = selectedRef.current;
    if (sel && prev[sel.r] && prev[sel.r][sel.c]) setEditValue(prev[sel.r][sel.c].value);
  }, []);
  const redo = useCallback(() => {
    const h = history.current;
    if (!h.future.length) return;
    const next = h.future.pop();
    h.past.push(gridRef.current);
    gridRef.current = next;
    setGrid(next);
    const sel = selectedRef.current;
    if (sel && next[sel.r] && next[sel.r][sel.c]) setEditValue(next[sel.r][sel.c].value);
  }, []);

  const setCell = useCallback(
    (r, c, value) => {
      mutateGrid((prev) =>
        prev.map((row, ri) =>
          ri === r ? row.map((cell, ci) => (ci === c ? cellWithValue(cell, value) : cell)) : row
        )
      );
    },
    [mutateGrid]
  );

  // Commit the popover edit into the grid (called on save / navigate / close).
  const commitEdit = useCallback(() => {
    const sel = selectedRef.current;
    if (!sel) return;
    const cur = gridRef.current[sel.r] && gridRef.current[sel.r][sel.c];
    if (cur && cur.value !== editValueRef.current) {
      setCell(sel.r, sel.c, editValueRef.current);
    }
  }, [setCell]);

  // ---- autosave ----
  useEffect(() => {
    try {
      const saved = JSON.parse(localStorage.getItem(STORAGE_KEY) || "null");
      if (saved && Array.isArray(saved.grid)) {
        const g = saved.grid.map((row) =>
          row.map((c) => {
            const cell = makeCell(c.raw, cleanBg(c.bg));
            if (c.synthetic) cell.synthetic = true;
            if (typeof c.o === "string") cell.orig = c.o;
            return cell;
          })
        );
        setGrid(g);
        gridRef.current = g;
        if (Array.isArray(saved.keyCols) && saved.keyCols.length) setKeyCols(saved.keyCols);
        else if (typeof saved.idCol === "number") setKeyCols([saved.idCol]); // legacy save
        if (saved.keyColsManual || saved.idColManual) setKeyColsManual(true);
        if (saved.fillMode === "row" || saved.fillMode === "field") setFillMode(saved.fillMode);
        if (saved.hideUnstyled) setHideUnstyled(true);
        if (saved.mode === "ru" || saved.mode === "kr") setMode(saved.mode);
        if (typeof saved.autoGt === "boolean") setAutoGt(saved.autoGt);
        if (Array.isArray(saved.ignoredKeys)) setIgnoredKeys(new Set(saved.ignoredKeys));
        if (Array.isArray(saved.diffCols)) setDiffCols(saved.diffCols.filter((c) => typeof c === "number"));
        if (saved.savedAt) setLastSaved(saved.savedAt);
      }
      const sel = JSON.parse(localStorage.getItem(SEL_KEY) || "null");
      if (sel && typeof sel.r === "number" && typeof sel.c === "number") {
        pendingSelectRef.current = sel;
      }
    } catch (e) {
      /* ignore */
    }
    restored.current = true;
  }, []);

  // Restore the cursor once the grid is loaded.
  useEffect(() => {
    const p = pendingSelectRef.current;
    if (p && gridRef.current.length && gridRef.current[p.r] && gridRef.current[p.r][p.c]) {
      pendingSelectRef.current = null;
      openCell(p.r, p.c, true);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [grid]);

  // Persist the cursor position separately (cheap; doesn't re-serialize the grid).
  useEffect(() => {
    if (!restored.current) return;
    try {
      if (selected) localStorage.setItem(SEL_KEY, JSON.stringify(selected));
      else localStorage.removeItem(SEL_KEY);
    } catch (e) {
      /* ignore */
    }
  }, [selected]);

  // Guard against accidentally closing/reloading the tab (e.g. Ctrl+W) while a
  // sheet is loaded — the browser shows its native "Leave site?" confirmation.
  useEffect(() => {
    if (!grid.length) return undefined;
    const onBeforeUnload = (e) => {
      e.preventDefault();
      e.returnValue = "";
      return "";
    };
    window.addEventListener("beforeunload", onBeforeUnload);
    return () => window.removeEventListener("beforeunload", onBeforeUnload);
  }, [grid.length]);

  useEffect(() => {
    if (!restored.current) return;
    const t = setTimeout(() => {
      try {
        const savedAt = Date.now();
        localStorage.setItem(
          STORAGE_KEY,
          JSON.stringify({
            grid: grid.map((row) =>
              row.map((c) =>
                c.synthetic
                  ? { raw: c.raw, bg: c.bg, synthetic: true }
                  : c.orig !== undefined && c.orig !== c.value
                  ? { raw: c.raw, bg: c.bg, o: c.orig }
                  : { raw: c.raw, bg: c.bg }
              )
            ),
            keyCols,
            keyColsManual,
            fillMode,
            hideUnstyled,
            mode,
            autoGt,
            ignoredKeys: [...ignoredKeys],
            diffCols,
            savedAt,
          })
        );
        setLastSaved(savedAt);
      } catch (e) {
        /* quota — ignore */
      }
    }, 400);
    return () => clearTimeout(t);
  }, [grid, keyCols, keyColsManual, fillMode, hideUnstyled, mode, autoGt, ignoredKeys, diffCols]);

  const detectIdCol = useCallback((g, byId) => {
    const cols = g.reduce((m, r) => Math.max(m, r.length), 0);
    for (let c = 0; c < cols; c++) {
      for (const row of g) {
        if (row[c] && !row[c].isMarker && row[c].key === "id") return c;
      }
    }
    let best = 0;
    let bestScore = -1;
    for (let c = 0; c < cols; c++) {
      let score = 0;
      for (const row of g) {
        const v = ((row[c] && row[c].value) || "").trim();
        if (!v) continue;
        if (byId && byId.has(v)) score += 2;
        else if (/^\d+$/.test(v)) score += 1;
      }
      if (score > bestScore) {
        bestScore = score;
        best = c;
      }
    }
    return best;
  }, []);

  // Pick the key column(s): a whole KEY_SET when every column of it is present
  // (skills), otherwise the single best id column.
  const detectKeyCols = useCallback(
    (g, idOnly) => {
      const cols = g.reduce((m, r) => Math.max(m, r.length), 0);
      const keyOf = (c) => {
        for (const row of g) if (row[c] && !row[c].isMarker && row[c].key) return row[c].key;
        return null;
      };
      const keysByCol = Array.from({ length: cols }, (_, c) => keyOf(c));
      for (const set of KEY_SETS) {
        const idx = set.map((k) => keysByCol.indexOf(k));
        if (idx.every((i) => i >= 0)) return idx;
      }
      return [detectIdCol(g, idOnly)];
    },
    [detectIdCol]
  );

  const loadGrid = useCallback(
    (rawRows) => {
      if (!rawRows.length) {
        wbToast("error", "Nothing to paste.");
        return;
      }
      const cells = rawRows.map((row) => row.map((c) => makeCell(c.text, c.bg)));
      const enchantedPaste = cells.some((row) =>
        row.some((c) => c.isMarker && c.raw === "enchanted_item_skill_begin")
      );
      const g = enchantedPaste ? alignEnchantedGrid(cells) : normalizeGrid(cells);
      history.current = { past: [], future: [] };
      gridRef.current = g;
      setGrid(g);
      setSelected(null);
      setPopover(false);
      // New grid: scroll the virtualized window back to the top.
      if (tableRef.current) tableRef.current.scrollTop = 0;
      setView((v) => ({ ...v, top: 0 }));
      if (!keyColsManual) {
        if (enchantedPaste) {
          let ic = 0;
          for (const row of g) {
            const i = row.findIndex((c) => c.key === "item_id");
            if (i >= 0) {
              ic = i;
              break;
            }
          }
          setKeyCols([ic]);
        } else {
          const idSource = english || altRef; // ids are the same in every language
          const idOnly =
            idSource && Array.isArray(idSource.records)
              ? new Map(
                  idSource.records.map((r) => [String(r.id == null ? "" : r.id).trim(), r])
                )
              : null;
          setKeyCols(detectKeyCols(g, idOnly));
        }
      }
    },
    [english, altRef, keyColsManual, detectKeyCols, wbToast]
  );

  const onSheetPaste = (e) => {
    e.preventDefault();
    const html = e.clipboardData.getData("text/html");
    const plain = e.clipboardData.getData("text/plain");
    loadGrid(html ? parseSheetHtml(html) : parseTsv(plain));
  };

  const handleEnglishFile = (files) => {
    const file = files && files[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = (ev) => {
      const parsed = parseData(String(ev.target.result));
      if (parsed.type === "Unknown") {
        wbToast("error", "Could not detect the English data type.");
        return;
      }
      setEnglish({ type: parsed.type, format: parsed.format, records: parsed.records });
      setEnglishName(file.name);
      // Enchanted data uses a composite key, not a single id column, so the
      // id-based column detection doesn't apply there.
      if (!keyColsManual && grid.length && parsed.type !== "Enchanted Item Skill") {
        const idOnly = new Map(
          parsed.records.map((r) => [String(r.id == null ? "" : r.id).trim(), r])
        );
        setKeyCols(detectKeyCols(grid, idOnly));
      }
    };
    reader.readAsText(file);
  };

  const handleAltRefFile = (files) => {
    const file = files && files[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = (ev) => {
      const parsed = parseData(String(ev.target.result));
      if (parsed.type === "Unknown") {
        wbToast("error", "Could not detect the reference data type.");
        return;
      }
      setAltRef({ type: parsed.type, format: parsed.format, records: parsed.records });
      setAltRefName(file.name);
      // Without an English file this one supplies the ids for key-column detection.
      if (!english && !keyColsManual && grid.length && parsed.type !== "Enchanted Item Skill") {
        const idOnly = new Map(
          parsed.records.map((r) => [String(r.id == null ? "" : r.id).trim(), r])
        );
        setKeyCols(detectKeyCols(grid, idOnly));
      }
    };
    reader.readAsText(file);
  };

  const handleOldRefFile = (files) => {
    const file = files && files[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = (ev) => {
      const parsed = parseData(String(ev.target.result));
      if (parsed.type === "Unknown") {
        wbToast("error", "Could not detect the old client's data type.");
        return;
      }
      setOldRef({ type: parsed.type, format: parsed.format, records: parsed.records });
      setOldRefName(file.name);
    };
    reader.readAsText(file);
  };

  // ---- selection ----
  const fetchGT = useCallback(
    (text) => {
      // Invalidate any in-flight request so a slow ack for a previous cell can't
      // overwrite the current cell's translation.
      const id = ++gtReqId.current;
      const cfg = MODE_CFG[mode] || MODE_CFG.kr;
      const hasSource = mode === "ru" ? /[a-zA-Z]/.test(text) : isKorean(text);
      if (!text || !hasSource) {
        setGt({ status: "none" });
        return;
      }
      const cacheKey = `${cfg.gtSl}:${cfg.gtTl}:${text}`;
      if (gtCache.current.has(cacheKey)) {
        setGt({ status: "done", text: gtCache.current.get(cacheKey) });
        return;
      }
      if (!socket || !isSocketReady) {
        setGt({ status: "offline" });
        return;
      }
      setGt({ status: "loading" });
      socket.emit("gtranslate", { text, sl: cfg.gtSl, tl: cfg.gtTl }, (resp) => {
        if (id !== gtReqId.current) return; // stale — selection moved on
        if (resp && resp.translation != null) {
          gtCache.current.set(cacheKey, resp.translation);
          setGt({ status: "done", text: resp.translation });
        } else {
          setGt({ status: "error" });
        }
      });
    },
    [socket, isSocketReady, mode]
  );

  const openCell = useCallback(
    (r, c, scroll) => {
      commitEdit();
      const cell = gridRef.current[r] && gridRef.current[r][c];
      const text = (cell && cell.value) || "";
      setSelected({ r, c });
      selectedRef.current = { r, c };
      setEditValue(text);
      editValueRef.current = text;
      selWasGreenRef.current = cell ? cellNeedsWork(cell, text) : false;
      tmReqId.current += 1; // invalidate any in-flight term-memory result
      setTm({ status: "idle" });
      if (autoGtRef.current) fetchGT(text);
      else setGt({ status: "idle" });
      setPopover(true);

      if (scroll) {
        // Rows are virtualized, so position the scroll container by row index
        // rather than relying on the cell already being in the DOM.
        const wrap = tableRef.current;
        const pos = visRowIdxRef.current.indexOf(r);
        if (wrap && pos >= 0) {
          const h = rowHRef.current || 68;
          wrap.scrollTop = Math.max(0, pos * h - wrap.clientHeight / 2 + h / 2);
          setView({ top: wrap.scrollTop, h: wrap.clientHeight });
        }
      }
    },
    [commitEdit, fetchGT, cellNeedsWork]
  );

  const closePopover = useCallback(() => {
    commitEdit();
    setSelected(null);
    setPopover(false);
  }, [commitEdit]);

  const applyValue = (value) => {
    const sel = selectedRef.current;
    if (!sel) return;
    const v = value == null ? "" : String(value);
    setCell(sel.r, sel.c, v);
    setEditValue(v);
    editValueRef.current = v;
  };

  // ---- right-click name lookup in the edit box ----
  const lookupVer = useMemo(
    () => (versions && versions.length ? Math.max(...versions.map((v) => v.id)) : 474),
    [versions]
  );

  const nameLookup = useCallback(
    (searchStr, x, y, selStart, selEnd) => {
      if (!socket || !isSocketReady) {
        wbToast("error", "Server offline — lookup unavailable.");
        return;
      }
      const reqId = `wb-lookup-${++lookupIdRef.current}`;
      setCtxMenu({
        x: Math.min(x, window.innerWidth - 360),
        y: Math.min(y, window.innerHeight - 320),
        searchStr,
        selStart,
        selEnd,
        reqId,
        loading: true,
        results: [],
      });

      const found = [];
      const onFound = (item) => {
        if (item && item.requestId === reqId) found.push(item);
      };
      let timer;
      const finish = (list) => {
        socket.off("itemFound", onFound);
        socket.off("endOfResults", onEnd);
        socket.off("error", onErr);
        clearTimeout(timer);
        setCtxMenu((cm) => (cm && cm.reqId === reqId ? { ...cm, loading: false, results: list } : cm));
      };
      const onEnd = (data) => {
        if (data && data.requestId === reqId) finish(found.slice(0, 50));
      };
      const onErr = (msg) => {
        if (msg && msg.requestId === reqId) finish([]);
      };
      timer = setTimeout(() => finish(found.slice(0, 50)), 8000);
      socket.on("itemFound", onFound);
      socket.on("endOfResults", onEnd);
      socket.on("error", onErr);
      socket.emit("startItemSearch", {
        searchVer: lookupVer,
        requestId: reqId,
        searchType: "names",
        searchStr,
        inLang: (MODE_CFG[mode] || MODE_CFG.kr).lookupIn,
        outLang: (MODE_CFG[mode] || MODE_CFG.kr).lookupOut,
        isSingleLanguage: false,
        useRawOutput: false,
      });
    },
    [socket, isSocketReady, lookupVer, wbToast, mode]
  );

  // ---- Monaco cell editor ----
  // A cell stores its line breaks as the literal two characters "\n". The editor
  // shows them as real lines and converts them back on every change, so
  // `editValue` - and therefore what gets saved - always stays in the raw form.
  const toDisplayText = (raw) => (raw || "").replace(/\\n/g, "\n");
  const toRawText = (disp) => (disp || "").replace(/\r\n/g, "\n").replace(/\n/g, "\\n");
  useEffect(() => {
    nameLookupRef.current = nameLookup;
  }, [nameLookup]);

  const onMonacoMount = (editor, monaco) => {
    monacoRef.current = editor;
    // Show every line without scrolling while it fits: the box grows with the
    // content (wrapped lines included) between 170px and 60% of the window.
    const fitHeight = () => {
      const box = monacoBoxRef.current;
      if (!box) return;
      // The wrapper creates the editor while its container is still hidden, so
      // the first layout is a bogus 5x5 (every character wraps): wait for a real one.
      if (editor.getLayoutInfo().width < 60) return;
      const max = Math.max(260, Math.round(window.innerHeight * 0.6));
      const h = Math.min(max, Math.max(170, editor.getContentHeight() + 8));
      if (box.style.height !== `${h}px`) {
        box.style.height = `${h}px`;
        editor.layout(); // the box changed: re-measure now
      }
    };
    editor.onDidContentSizeChange(fitHeight);
    editor.onDidLayoutChange(fitHeight); // a new width re-wraps the lines
    // Measure explicitly once the container is shown, instead of relying on the
    // resize observer alone.
    setTimeout(() => {
      if (monacoRef.current !== editor) return;
      editor.layout();
      fitHeight();
    }, 0);
    // Alternate shading per LOGICAL line: the bands sit exactly on the lines, a
    // wrapped line keeps one shade, and scrolling cannot misalign them (a striped
    // background image can do none of that).
    const bands = editor.createDecorationsCollection ? editor.createDecorationsCollection([]) : null;
    let bandIds = [];
    let bandLines = -1;
    const shadeLines = () => {
      const model = editor.getModel();
      if (!model || model.getLineCount() === bandLines) return;
      bandLines = model.getLineCount();
      const next = [];
      for (let ln = 2; ln <= bandLines; ln += 2) {
        next.push({
          range: new monaco.Range(ln, 1, ln, 1),
          options: { isWholeLine: true, className: "wb-line-alt" },
        });
      }
      if (bands) bands.set(next);
      else bandIds = editor.deltaDecorations(bandIds, next);
    };
    editor.onDidChangeModelContent(shadeLines);
    shadeLines();
    // Same highlighting as the Translation Helper, under the Workbench's OWN
    // language id: the Helper registers a completion provider for its language
    // that inserts terms as snippets, which drops $sN variables ("$s2" -> "s2").
    // It stays registered after leaving that page, so sharing the id would give
    // this editor those completions too.
    if (!monaco.languages.getLanguages().some((l) => l.id === "l2dat")) {
      monaco.languages.register({ id: "l2dat" });
    }
    monaco.languages.setMonarchTokensProvider("l2dat", {
      tokenizer: {
        root: [
          [/\$s[0-9]+/, "variable"],
          [/\d+/, "variable"],
          [/%/, "variable"],
          [/\+/, "variable"],
          [/-/, "variable"],
          [/<[a-zA-Z0-9]+\s[a-zA-Z0-9]+=["]{0,1}[a-zA-Z0-9]+["]{0,1}>/, "tag"],
          [/<\/[a-zA-Z0-9]+>/, "tag"],
        ],
      },
    });
    // Right-click a selection -> "Look up name" (what the plain box does on
    // right-click). Offsets are mapped back to the raw text, where a line break
    // is two characters, so the replacement lands in the right place.
    editor.addAction({
      id: "wb-name-lookup",
      label: "Look up name",
      contextMenuGroupId: "navigation",
      contextMenuOrder: 0,
      precondition: "editorHasSelection",
      run: (ed) => {
        const model = ed.getModel();
        const sel = ed.getSelection();
        if (!model || !sel) return;
        const text = model.getValueInRange(sel).trim();
        if (!text || !nameLookupRef.current) return;
        const all = model.getValue();
        const rawOffset = (pos) => toRawText(all.slice(0, model.getOffsetAt(pos))).length;
        const box = ed.getDomNode().getBoundingClientRect();
        const at = ed.getScrolledVisiblePosition(sel.getEndPosition());
        nameLookupRef.current(
          text,
          box.left + (at ? at.left : 0),
          box.top + (at ? at.top + at.height : 0),
          rawOffset(sel.getStartPosition()),
          rawOffset(sel.getEndPosition())
        );
      },
    });
  };
  const onMonacoChange = (value) => {
    const raw = toRawText(value);
    if (raw === editValueRef.current) return; // our own setValue echoing back
    editValueRef.current = raw;
    setEditValue(raw);
  };
  // Changes that come from outside the editor (another cell, Apply reference /
  // Google Translate / term memory, a name replacement, a mass replace) are pushed
  // into it; its own edits already match and are left alone (cursor untouched).
  useEffect(() => {
    const ed = monacoRef.current;
    if (!ed || editorKind !== "monaco") return;
    const model = ed.getModel && ed.getModel();
    if (!model) return;
    const want = toDisplayText(editValue);
    if (model.getValue().replace(/\r\n/g, "\n") !== want) ed.setValue(want);
  }, [editValue, editorKind]);
  // The instance dies with the sidebar and with the switch to the plain box.
  useEffect(() => {
    if (!(selected && popover && editorKind === "monaco")) monacoRef.current = null;
  }, [selected, popover, editorKind]);

  const onEditContextMenu = (e) => {
    const ta = e.target;
    const start = ta.selectionStart;
    const end = ta.selectionEnd;
    if (start === end) return; // no selection → allow the native menu
    const sel = editValue.slice(start, end).trim();
    if (!sel) return;
    e.preventDefault();
    nameLookup(sel, e.clientX, e.clientY, start, end);
  };

  const replaceSelection = (replacement) => {
    const cm = ctxMenu;
    if (!cm || !replacement) return;
    const nv = editValue.slice(0, cm.selStart) + replacement + editValue.slice(cm.selEnd);
    setEditValue(nv);
    editValueRef.current = nv;
    setCtxMenu(null);
  };

  // Close the lookup menu on outside click or Escape (capture so it wins over the
  // popover's own Escape handler).
  useEffect(() => {
    if (!ctxMenu) return;
    const onDown = (e) => {
      if (!e.target.closest(".wb-ctx")) setCtxMenu(null);
    };
    const onEsc = (e) => {
      if (e.key === "Escape") {
        e.stopPropagation();
        setCtxMenu(null);
      }
    };
    document.addEventListener("mousedown", onDown);
    document.addEventListener("keydown", onEsc, true);
    return () => {
      document.removeEventListener("mousedown", onDown);
      document.removeEventListener("keydown", onEsc, true);
    };
  }, [ctxMenu]);

  // ---- reference (equivalent column only) ----
  // For enchanted skills the key is item_id|RequiredEnchant; otherwise the id column.
  // Labels for the second (non-English) reference + auto-fill button, per mode.
  const altLangLabel = mode === "ru" ? "Russian" : "Korean";
  const srcLangLabel = mode === "ru" ? "English" : "Korean"; // the language translated FROM
  const autoFillSrc = mode === "ru" ? "EN" : "KR"; // pasted data's language
  const autoFillTgt = mode === "ru" ? "RU" : "EN"; // translation to copy in

  const rowId = selected ? rowLookupKey(selected.r) : "";
  // The reference under the edit box is the TARGET-language file: the English
  // .txt in KR->EN, the Russian .txt in EN->RU (where the pasted data already IS
  // the English text, so the English file would only echo the cell).
  const refFile = mode === "ru" ? altRef : english;
  const refLangLabel = mode === "ru" ? "Russian" : "English";
  const record = rowId ? targetIdx.get(rowId) || null : null;
  const selKey = selected && columnKeys[selected.c] ? columnKeys[selected.c] : null;
  const refValue =
    record && selKey && record.fields && record.fields[selKey] !== undefined
      ? record.fields[selKey]
      : null;
  useEffect(() => {
    refValueRef.current = refValue;
  }, [refValue]);

  // Live sanity checks of the selected cell against the reference column. Kept
  // terse (cell ≠ reference) so a mismatch is easy to spot at a glance.
  const warnings = useMemo(() => {
    if (refValue == null || !selected) return [];
    const w = [];
    const cellNl = countNewlines(editValue);
    const refNl = countNewlines(refValue);
    if (cellNl !== refNl) w.push({ kind: "nl", text: `Lines ${cellNl}≠${refNl}` });

    const cellFont = countFontTags(editValue);
    const refFont = countFontTags(refValue);
    if (cellFont !== refFont) w.push({ kind: "font", text: `Fonts ${cellFont}≠${refFont}` });

    const { extra, missing } = multisetDiff(extractNumbers(editValue), extractNumbers(refValue));
    if (extra.length || missing.length) {
      const parts = [];
      if (extra.length) parts.push(`+${extra.join(",")}`);
      if (missing.length) parts.push(`−${missing.join(",")}`);
      w.push({ kind: "num", text: `Numbers ${parts.join(" ")}` });
    }
    return w;
  }, [editValue, refValue, selected]);

  // The lines those chips are about, highlighted in the preview (nothing when
  // the cell matches its reference).
  const badLines = useMemo(
    () =>
      warnings.length && refValue != null
        ? mismatchedLines(editValue, refValue)
        : { cell: new Set(), ref: new Set() },
    [warnings, editValue, refValue]
  );
  const cellPreviewHtml = useMemo(
    () => (showPreview ? prettyPreview(editValue, badLines.cell) : ""),
    [showPreview, editValue, badLines]
  );
  const refPreviewHtml = useMemo(
    () => (showPreview && refValue != null ? prettyPreview(refValue, badLines.ref) : ""),
    [showPreview, refValue, badLines]
  );
  // Old client vs new client, for the selected field. The NEW source text comes
  // from the new source-language reference (KR file in KR->EN, EN file in EN->RU)
  // so an already-translated cell still compares source to source; without that
  // file the cell's current text stands in.
  const oldRec = rowId ? oldIndex.get(rowId) || null : null;
  const oldSrc =
    oldRec && selKey && oldRec.fields && oldRec.fields[selKey] !== undefined ? oldRec.fields[selKey] : null;
  const newSrcRec = rowId ? (mode === "ru" ? englishIndex : altIndex).get(rowId) || null : null;
  const newSrc =
    newSrcRec && selKey && newSrcRec.fields && newSrcRec.fields[selKey] !== undefined
      ? newSrcRec.fields[selKey]
      : null;
  const clientDiff = useMemo(() => {
    if (!showPreview || oldSrc == null) return null;
    const a = toDisplayText(oldSrc);
    const b = toDisplayText(newSrc == null ? editValue : newSrc);
    if (a === b) return { same: true };
    const segs = diffChars(a, b);
    return { same: false, beforeHtml: prettyDiffHtml(segs, "before"), afterHtml: prettyDiffHtml(segs, "after") };
  }, [showPreview, oldSrc, newSrc, editValue]);

  // Pre-check: for every still-untranslated string cell, compare its structure
  // (line count, font tags, numbers) against the target-language reference and
  // flag mismatches so they stand out in the grid before you even open them.
  const warnedCells = useMemo(() => {
    const set = new Set();
    if (!targetIdx.size) return set;
    for (let r = 0; r < grid.length; r++) {
      const key = rowLookupKey(r);
      if (!key) continue;
      const rec = targetIdx.get(key);
      if (!rec || !rec.fields) continue;
      const row = grid[r];
      for (let c = 0; c < row.length; c++) {
        const cell = row[c];
        if (!isStringCell(cell) || !cellNeedsWork(cell)) continue;
        const colKey = columnKeys[c];
        const ref = colKey ? rec.fields[colKey] : undefined;
        if (ref == null) continue;
        const val = cell.value;
        const { extra, missing } = multisetDiff(extractNumbers(val), extractNumbers(ref));
        if (
          countNewlines(val) !== countNewlines(ref) ||
          countFontTags(val) !== countFontTags(ref) ||
          extra.length ||
          missing.length
        ) {
          set.add(`${r},${c}`);
        }
      }
    }
    return set;
  }, [grid, targetIdx, rowLookupKey, columnKeys, isStringCell, cellNeedsWork]);

  const applyReference = useCallback(() => {
    const sel = selectedRef.current;
    if (!sel) return;
    const rec = targetIdx.get(rowLookupKey(sel.r)); // the file the panel shows
    const key = columnKeys[sel.c];
    if (rec && key && rec.fields && rec.fields[key] !== undefined) {
      applyValue(rec.fields[key]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [targetIdx, rowLookupKey, columnKeys]);

  // ---- occurrences ----
  // Cells are grouped by their PRISTINE text (cell.orig, the value as pasted),
  // not by their current text: a half-finished edit that got saved while you
  // copied something from another skill still belongs to its group, so "Apply
  // this to all" keeps working when you come back to it.
  const occurrenceInfo = useMemo(() => {
    const none = { total: 0, count: 0, skipped: 0 };
    if (!selected) return none;
    const me = grid[selected.r] && grid[selected.r][selected.c];
    const key = me && !me.isMarker ? me.orig : "";
    if (!key) return none;
    const mine = me.value; // this cell's committed text
    let total = 0;
    let count = 0;
    for (let r = 0; r < grid.length; r++) {
      for (let c = 0; c < grid[r].length; c++) {
        const cell = grid[r][c];
        if (cell.isMarker || cell.synthetic || cell.orig !== key) continue;
        total += 1;
        // Targets: untouched cells, cells already in step with this one, and this cell.
        if (cell.value === key || cell.value === mine || (r === selected.r && c === selected.c)) count += 1;
      }
    }
    return { total, count, skipped: total - count };
  }, [grid, selected]);
  const occurrences = occurrenceInfo.total;

  // Write `val` into the selected cell's group. `force` also overwrites the cells
  // that were edited separately (text differs from both the pristine text and
  // this cell's). Returns how many cells changed.
  const applyToGroup = useCallback(
    (val, force) => {
      const sel = selectedRef.current;
      const g = gridRef.current;
      const me = sel && g[sel.r] && g[sel.r][sel.c];
      const key = me && !me.isMarker ? me.orig : "";
      if (!key) return 0;
      const mine = me.value;
      let applied = 0;
      mutateGrid((prev) =>
        prev.map((row, r) => {
          let changed = false;
          const next = row.map((cell, c) => {
            if (cell.isMarker || cell.synthetic || cell.orig !== key) return cell;
            const isMe = r === sel.r && c === sel.c;
            if (!force && !isMe && cell.value !== key && cell.value !== mine) return cell;
            if (cell.value === val) return cell;
            applied += 1;
            changed = true;
            return cellWithValue(cell, val);
          });
          return changed ? next : row;
        })
      );
      setEditValue(val);
      editValueRef.current = val;
      return applied;
    },
    [mutateGrid]
  );

  const replaceOccurrences = (force) => {
    const applied = applyToGroup(editValueRef.current, !!force);
    wbToast("success", `Applied to ${applied} cell${applied === 1 ? "" : "s"}.`);
  };

  // Push the selected cell's reference string to its whole group - no copy/paste
  // into the edit box needed. Reads from refs so the Ctrl+Q shortcut can use it.
  const applyReferenceToOccurrences = useCallback(() => {
    const ref = refValueRef.current;
    if (ref == null) return;
    const applied = applyToGroup(String(ref), false);
    wbToast("success", `Applied reference to ${applied} cell${applied === 1 ? "" : "s"}.`);
  }, [applyToGroup, wbToast]);

  // For every row whose translatable cells ALL match the SOURCE-language
  // reference exactly (i.e. the source text is unchanged from this version), copy
  // the existing translation in from the TARGET-language reference. Works both
  // ways: KR→EN matches the KR ref and copies EN; EN→RU matches EN and copies RU.
  // `english` is always the English file; `altRef` is the non-English one.
  const autoFillUnchanged = useCallback(() => {
    commitEdit();
    // Source ref = the pasted data's own language; target ref = the translation.
    const sourceIdx = mode === "ru" ? englishIndex : altIndex; // EN | KR
    const targetIdx = mode === "ru" ? altIndex : englishIndex; // RU | EN
    if (!sourceIdx.size || !targetIdx.size) {
      wbToast("error", "Upload both reference files first.");
      return;
    }
    const g = gridRef.current;
    let filledRows = 0;
    let filledCells = 0;
    let filledByText = 0;
    // Second pass lookup: the same SOURCE text under ANY key of the source
    // reference -> that key's TARGET text. Built lazily per field. This is what
    // fills a NEW skill (no key in either reference) whose name or desc equals
    // an existing skill's: skill 105 is new, but its name is skill 100's.
    const byText = new Map();
    const targetByText = (field, text) => {
      let m = byText.get(field);
      if (!m) {
        m = new Map();
        for (const [k, rec] of sourceIdx) {
          const sv = rec.fields ? rec.fields[field] : undefined;
          if (sv === undefined || sv === "" || m.has(sv)) continue;
          const tgt = targetIdx.get(k);
          const tv = tgt && tgt.fields ? tgt.fields[field] : undefined;
          if (tv !== undefined && tv !== "") m.set(sv, tv);
        }
        byText.set(field, m);
      }
      return m.get(text);
    };
    // The filters scope the fill: only the rows currently shown are touched.
    const scoped = rowFilter !== "all" || hideUnstyled;
    const next = g.map((row, r) => {
      if (scoped && !rowVisible(row, r)) return row;
      const newRow = row.slice();
      let rowChanged = false;
      const key = rowLookupKey(r);
      const srcRec = key ? sourceIdx.get(key) : null;
      const tgtRec = key ? targetIdx.get(key) : null;

      // Pass 1, by KEY. Per FIELD: a translatable cell whose source text is
      // unchanged from the source-language reference gets its existing
      // translation, independently of its siblings - e.g. an unchanged `desc`
      // still fills when `name` gained a "미사용 - " prefix and has to be
      // translated by hand.
      if (srcRec && tgtRec && srcRec.fields && tgtRec.fields) {
        // "Whole row" mode: skip the row unless EVERY translatable cell is unchanged.
        let wholeRowOk = true;
        if (fillMode === "row") {
          for (let c = 0; c < row.length; c++) {
            const cell = row[c];
            if (!isStringCell(cell)) continue;
            const colKey = columnKeys[c];
            const srcVal = colKey ? srcRec.fields[colKey] : undefined;
            if (srcVal === undefined || cell.value !== srcVal) {
              wholeRowOk = false;
              break;
            }
          }
        }
        if (wholeRowOk) {
          for (let c = 0; c < row.length; c++) {
            const cell = row[c];
            if (!isStringCell(cell)) continue;
            const colKey = columnKeys[c];
            if (!colKey) continue;
            const srcVal = srcRec.fields[colKey];
            const tgtVal = tgtRec.fields[colKey];
            if (srcVal === undefined || tgtVal === undefined) continue;
            if (cell.value !== srcVal) continue; // this field changed - leave it for manual work
            if (newRow[c].value !== tgtVal) {
              newRow[c] = cellWithValue(newRow[c], tgtVal);
              filledCells += 1;
              rowChanged = true;
            }
          }
        }
      }

      // Pass 2, by TEXT, for whatever is still untranslated - including rows the
      // references do not know at all.
      for (let c = 0; c < newRow.length; c++) {
        const cell = newRow[c];
        if (!isStringCell(cell) || !cellNeedsWork(cell)) continue;
        const colKey = columnKeys[c];
        if (!colKey) continue;
        const tv = targetByText(colKey, cell.value);
        if (tv === undefined || tv === cell.value) continue;
        newRow[c] = cellWithValue(cell, tv);
        filledByText += 1;
        rowChanged = true;
      }

      if (rowChanged) filledRows += 1;
      return rowChanged ? newRow : row;
    });

    const scopeNote = scoped ? " (shown rows only)" : "";
    if (!filledRows) {
      wbToast("info", `Nothing to auto-fill: no unchanged cell by key and no matching text${scopeNote}.`);
      return;
    }
    mutateGrid(() => next);
    const parts = [];
    if (filledCells) parts.push(`${filledCells} by key`);
    if (filledByText) parts.push(`${filledByText} by matching text`);
    wbToast(
      "success",
      `Auto-filled ${parts.join(" and ")} across ${filledRows} row${filledRows === 1 ? "" : "s"}${scopeNote}.`
    );
  }, [
    mode,
    fillMode,
    englishIndex,
    altIndex,
    rowLookupKey,
    columnKeys,
    isStringCell,
    cellNeedsWork,
    mutateGrid,
    wbToast,
    commitEdit,
    rowFilter,
    hideUnstyled,
    rowVisible,
  ]);

  // ---- term memory (line-aware, pretty diff) ----
  const runTermMemory = () => {
    if (!selectedRef.current) return;
    if (!socket || !isSocketReady) {
      wbToast("error", "Server offline — term memory unavailable.");
      return;
    }
    const src = editValueRef.current.replace(/\\n/g, "\n");
    const id = ++tmReqId.current;
    setTm({ status: "loading" });
    // The answer comes back through the ack, so it always belongs to THIS
    // request. (A one-shot "translation" listener could pick up the reply to a
    // different, slower request - the wrong cell's text - and drop its own.)
    let settled = false;
    let timer = null;
    const finish = (data) => {
      if (settled) return;
      settled = true;
      clearTimeout(timer);
      socket.off("translation", onLegacyEvent);
      if (id !== tmReqId.current) return; // stale - the selection moved on
      if (data && data.text != null && !data.error) {
        setTm({ status: "done", before: src, after: data.text });
      } else {
        setTm({ status: "error", message: (data && data.error) || "" });
      }
    };
    // Servers that predate the ack answer with a "translation" event instead.
    const onLegacyEvent = (p) => finish(p && p.data);
    timer = setTimeout(() => finish({ error: "timed out waiting for the server" }), 45000);
    socket.on("translation", onLegacyEvent);
    socket.emit(
      "translate",
      { text: src, lang: (MODE_CFG[mode] || MODE_CFG.kr).termTarget },
      (resp) => finish(resp && resp.data)
    );
  };
  const applyTerm = () => {
    if (tm.status !== "done") return;
    applyValue(tm.after.replace(/\r?\n/g, "\\n"));
  };

  // ---- untranslated strings (scan the grid with the term database) ----
  // null | {status:"loading", done, total} | {status:"done", cells, missing, scoped, stamp, applied}
  const [scan, setScan] = useState(null);
  const scanReq = useRef(0);

  const runScan = () => {
    commitEdit();
    if (!socket || !isSocketReady) {
      wbToast("error", "Server offline - the scan needs the term database.");
      return;
    }
    const g = gridRef.current;
    const scoped = rowFilter !== "all" || hideUnstyled; // filters scope the scan too
    const cells = [];
    for (let r = 0; r < g.length; r++) {
      if (scoped && !rowVisible(g[r], r)) continue;
      for (let c = 0; c < g[r].length; c++) {
        const cell = g[r][c];
        if (isStringCell(cell) && cellNeedsWork(cell)) cells.push({ r, c, src: cell.value, raw: cell.raw });
      }
    }
    if (!cells.length) {
      wbToast("info", `No untranslated cells${scoped ? " among the rows shown" : ""}.`);
      return;
    }
    // Skill data writes "%" as "%%"; tell the server which convention to keep.
    const dbl = cells.some((x) => x.src.includes("%%"));
    const sgl = cells.some((x) => /(^|[^%])%([^%]|$)/.test(x.src));
    const type =
      dbl && !sgl ? "skill" : sgl && !dbl ? "item" : columnKeys.includes("skill_id") ? "skill" : "item";
    const langs = mode === "ru" ? { inLang: "EU", outLang: "RU" } : { inLang: "KR", outLang: "EU" };
    const my = ++scanReq.current;
    const requestId = `wbscan-${Date.now()}-${my}`;
    setScan({ status: "loading", done: 0, total: cells.length });

    let settled = false;
    let watchdog = null;
    const cleanup = () => {
      clearTimeout(watchdog);
      socket.off("batchProgress", onProgress);
    };
    const fail = (msg) => {
      if (settled) return;
      settled = true;
      cleanup();
      if (my !== scanReq.current) return;
      setScan(null);
      wbToast("error", msg);
    };
    const arm = () => {
      clearTimeout(watchdog);
      watchdog = setTimeout(() => fail("The server went silent for 60s during the scan."), 60000);
    };
    function onProgress(p) {
      if (settled || !p || p.requestId !== requestId) return;
      arm();
      if (my === scanReq.current) {
        setScan((s) => (s && s.status === "loading" ? { ...s, done: p.done, total: p.total } : s));
      }
    }
    socket.on("batchProgress", onProgress);
    arm();
    // One single-column "string" grid: the batch translator dedupes identical
    // cells, counts every occurrence of an untranslated line and streams progress.
    socket.emit(
      "batchTranslate",
      { rows: cells.map((x) => [x.raw]), colRoles: ["string"], ...langs, type, requestId },
      (resp) => {
        if (settled) return;
        settled = true;
        cleanup();
        if (my !== scanReq.current) return;
        if (!resp || resp.error) {
          setScan(null);
          wbToast("error", `Scan failed: ${(resp && resp.error) || "no response from the server"}`);
          return;
        }
        const out = cells.map((x, i) => {
          const res = resp.rows && resp.rows[i] && resp.rows[i][0];
          const t = res && typeof res.t === "string" ? parseCell(res.t) : null;
          return { r: x.r, c: x.c, src: x.src, out: t && !t.isMarker ? t.value : x.src, ok: !!(res && res.s === "ok") };
        });
        setScan({ status: "done", cells: out, missing: resp.missing || [], scoped, stamp: Date.now(), applied: 0 });
      }
    );
  };

  // Write every translation the scan produced into the grid (one undo step).
  // A cell edited since the scan is left alone.
  const applyScan = () => {
    if (!scan || scan.status !== "done") return;
    const byPos = new Map();
    for (const x of scan.cells) if (x.out !== x.src) byPos.set(`${x.r},${x.c}`, x);
    if (!byPos.size) {
      wbToast("info", "Nothing to apply - the term database changed no cell.");
      return;
    }
    commitEdit();
    let applied = 0;
    mutateGrid((prev) =>
      prev.map((row, r) => {
        let changed = false;
        const next = row.map((cell, c) => {
          const hit = byPos.get(`${r},${c}`);
          if (!hit || cell.value !== hit.src) return cell;
          changed = true;
          applied += 1;
          return cellWithValue(cell, hit.out);
        });
        return changed ? next : row;
      })
    );
    // The selected cell may have changed under the editor: reload it.
    const sel = selectedRef.current;
    if (sel && byPos.has(`${sel.r},${sel.c}`)) {
      const v = (gridRef.current[sel.r][sel.c] || {}).value || "";
      setEditValue(v);
      editValueRef.current = v;
    }
    setScan((s) =>
      s && s.status === "done"
        ? { ...s, applied: s.applied + applied, cells: s.cells.map((x) => ({ ...x, src: x.out })) }
        : s
    );
    wbToast("success", `Applied term translations to ${applied} cell${applied === 1 ? "" : "s"} (Ctrl+Z undoes it).`);
  };

  // Escape closes the scan dialog first (capture, so the popover stays open).
  useEffect(() => {
    if (!scan || scan.status === "loading") return undefined;
    const onEsc = (e) => {
      if (e.key === "Escape") {
        e.stopPropagation();
        setScan(null);
      }
    };
    document.addEventListener("keydown", onEsc, true);
    return () => document.removeEventListener("keydown", onEsc, true);
  }, [scan]);

  // "Differs from reference" mode: overwrite the compared columns of the rows
  // shown with the target reference's text (one undo step).
  const takeReferenceForShown = () => {
    if (!targetIdx.size) return;
    commitEdit();
    const g = gridRef.current;
    let cells = 0;
    let rows = 0;
    const next = g.map((row, r) => {
      if (!rowVisible(row, r)) return row;
      const rec = targetIdx.get(rowLookupKey(r));
      if (!rec || !rec.fields) return row;
      let changed = false;
      const nr = row.map((cell, c) => {
        if (!diffColsEff.includes(c) || cell.isMarker) return cell;
        const k = columnKeys[c];
        const rv = k ? rec.fields[k] : undefined;
        if (rv === undefined || (cell.value || "") === String(rv)) return cell;
        cells += 1;
        changed = true;
        return cellWithValue(cell, String(rv));
      });
      if (changed) rows += 1;
      return changed ? nr : row;
    });
    if (!cells) {
      wbToast("info", "Nothing to take: the rows shown already match the reference.");
      return;
    }
    mutateGrid(() => next);
    const sel = selectedRef.current;
    if (sel && gridRef.current[sel.r] && gridRef.current[sel.r][sel.c]) {
      const v = gridRef.current[sel.r][sel.c].value || "";
      if (v !== editValueRef.current) {
        setEditValue(v);
        editValueRef.current = v;
      }
    }
    wbToast(
      "success",
      `Took the ${targetLabel} reference for ${cells} cell${cells === 1 ? "" : "s"} across ${rows} row${
        rows === 1 ? "" : "s"
      } (Ctrl+Z undoes it).`
    );
  };

  // ---- find & replace (floating panel; engine: buildFinder / replaceInText) ----
  const finder = useMemo(() => buildFinder(find, useRegex, matchCase), [find, useRegex, matchCase]);

  // Every cell the current search would change, with its hits. Used by both the
  // preview and the apply step.
  const collectReplacements = () => {
    const cells = [];
    let matches = 0;
    if (!finder.re) return { cells, matches };
    const g = gridRef.current;
    for (let r = 0; r < g.length; r++) {
      for (let c = 0; c < g[r].length; c++) {
        const cell = g[r][c];
        if (cell.isMarker || cell.synthetic || !cell.value) continue;
        const { out, hits } = replaceInText(cell.value, finder.re, replace, useRegex);
        if (!hits.length || out === cell.value) continue;
        matches += hits.length;
        cells.push({ r, c, before: cell.value, after: out, hits });
      }
    }
    return { cells, matches };
  };

  const previewReplace = () => {
    if (!finder.re) return;
    commitEdit();
    const res = collectReplacements();
    if (!res.cells.length) {
      wbToast("info", "No matches.");
      return;
    }
    setFrPreview(res);
  };

  const replaceAll = () => {
    if (!finder.re) return;
    commitEdit();
    const res = collectReplacements();
    setFrPreview(null);
    if (!res.cells.length) {
      wbToast("info", "No matches.");
      return;
    }
    const byPos = new Map(res.cells.map((x) => [`${x.r},${x.c}`, x]));
    mutateGrid((prev) =>
      prev.map((row, r) => {
        let changed = false;
        const next = row.map((cell, c) => {
          const hit = byPos.get(`${r},${c}`);
          if (!hit || cell.value !== hit.before) return cell;
          changed = true;
          return cellWithValue(cell, hit.after);
        });
        return changed ? next : row;
      })
    );
    // The open cell may have changed under the editor: reload it.
    const sel = selectedRef.current;
    if (sel && byPos.has(`${sel.r},${sel.c}`)) {
      const v = (gridRef.current[sel.r][sel.c] || {}).value || "";
      setEditValue(v);
      editValueRef.current = v;
    }
    wbToast(
      "success",
      `Replaced ${res.matches} occurrence${res.matches === 1 ? "" : "s"} in ${res.cells.length} cell${
        res.cells.length === 1 ? "" : "s"
      } (Ctrl+Z undoes it).`
    );
  };

  // Escape closes the replace preview first (capture, so the sidebar stays open).
  useEffect(() => {
    if (!frPreview) return undefined;
    const onEsc = (e) => {
      if (e.key === "Escape") {
        e.stopPropagation();
        setFrPreview(null);
      }
    };
    document.addEventListener("keydown", onEsc, true);
    return () => document.removeEventListener("keydown", onEsc, true);
  }, [frPreview]);

  // ---- copy ----
  // Flush the server's term cache so terms edited directly in the DB are used
  // immediately by term memory / auto-translation.
  const clearTermCache = () => {
    if (!socket || !isSocketReady) {
      wbToast("error", "Server offline.");
      return;
    }
    socket.emit("clearTranslationCache", {}, (resp) => {
      if (resp && resp.ok) wbToast("success", "Term cache cleared — edited terms are live.");
      else wbToast("error", "Could not clear the term cache.");
    });
  };

  const copyResult = async () => {
    commitEdit();
    const g = gridRef.current;
    if (!g.length) return;
    const esc = (s) =>
      (s || "").replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;");
    // Synthetic cells exist only to align columns for display — drop them so the
    // copied text matches the original file structure exactly.
    const realCells = (row) => row.filter((c) => !c.synthetic);
    const htmlRows = g
      .map((row) => {
        const tds = realCells(row)
          .map((cell) => {
            const bg = effectiveBg(cell);
            const style = bg ? ` style="background-color:${bg}"` : "";
            return `<td${style}>${esc(cell.raw)}</td>`;
          })
          .join("");
        return `<tr>${tds}</tr>`;
      })
      .join("");
    const html = `<meta charset="utf-8"><table>${htmlRows}</table>`;
    const tsv = g
      .map((row) =>
        realCells(row)
          .map((c) => (c.raw || "").replace(/\t/g, " ").replace(/\r?\n/g, " "))
          .join("\t")
      )
      .join("\n");
    try {
      if (navigator.clipboard && window.ClipboardItem) {
        await navigator.clipboard.write([
          new window.ClipboardItem({
            "text/html": new Blob([html], { type: "text/html" }),
            "text/plain": new Blob([tsv], { type: "text/plain" }),
          }),
        ]);
        wbToast("success", "Copied — paste into Google Sheets.");
      } else {
        await navigator.clipboard.writeText(tsv);
        wbToast("success", "Copied as plain text.");
      }
    } catch (err) {
      wbToast("error", "Copy failed: " + err.message);
    }
  };

  const clearAll = () => {
    history.current = { past: [], future: [] };
    gridRef.current = [];
    setGrid([]);
    setSelected(null);
    setPopover(false);
    setKeyColsManual(false);
    try {
      localStorage.removeItem(STORAGE_KEY);
    } catch (e) {
      /* ignore */
    }
  };

  // ---- prev/next unfinished row ----
  const gotoKorean = (dir) => {
    const g = gridRef.current;
    if (!g.length) return;
    const rows = [];
    for (let i = 0; i < g.length; i++) if (g[i].some((cell) => cellNeedsWork(cell))) rows.push(i);
    if (!rows.length) {
      wbToast("success", "No unfinished rows remain.");
      return;
    }
    const cur = selectedRef.current ? selectedRef.current.r : -1;
    let target;
    if (dir > 0) {
      target = rows.find((r) => r > cur);
      if (target === undefined) target = rows[0];
    } else {
      const before = rows.filter((r) => r < cur);
      target = before.length ? before[before.length - 1] : rows[rows.length - 1];
    }
    const col = g[target].findIndex((cell) => cellNeedsWork(cell));
    openCell(target, col < 0 ? keyCols[0] || 0 : col, true);
  };

  // ---- keyboard ----
  useEffect(() => {
    const onKey = (e) => {
      const tag = e.target && e.target.tagName;
      const inField = tag === "INPUT" || tag === "TEXTAREA" || tag === "SELECT";

      if (e.key === "Escape") {
        closePopover();
        return;
      }
      if ((e.ctrlKey || e.metaKey) && (e.key === "s" || e.key === "S")) {
        if (!selectedRef.current) return; // nothing open: leave the browser default
        e.preventDefault();
        commitEdit();
        return;
      }
      if ((e.ctrlKey || e.metaKey) && (e.key === "z" || e.key === "Z") && !e.shiftKey) {
        if (inField) return; // let native textarea undo work
        e.preventDefault();
        undo();
        return;
      }
      if (
        (e.ctrlKey || e.metaKey) &&
        (e.key === "y" || e.key === "Y" || ((e.key === "z" || e.key === "Z") && e.shiftKey))
      ) {
        if (inField) return;
        e.preventDefault();
        redo();
        return;
      }
      if ((e.ctrlKey || e.metaKey) && (e.key === "e" || e.key === "E")) {
        e.preventDefault();
        applyReference();
        return;
      }
      if ((e.ctrlKey || e.metaKey) && (e.key === "q" || e.key === "Q")) {
        e.preventDefault();
        applyReferenceToOccurrences();
        return;
      }
      if (!inField && selectedRef.current && e.key.startsWith("Arrow")) {
        e.preventDefault();
        const sel = selectedRef.current;
        const cPos = visCols.indexOf(sel.c);
        let nr = sel.r;
        let nc = sel.c;
        if (e.key === "ArrowUp") {
          const t = prevVisibleRow(sel.r);
          if (t >= 0) nr = t;
        } else if (e.key === "ArrowDown") {
          const t = nextVisibleRow(sel.r);
          if (t >= 0) nr = t;
        } else if (e.key === "ArrowLeft" && cPos > 0) {
          nc = visCols[cPos - 1];
        } else if (e.key === "ArrowRight" && cPos < visCols.length - 1) {
          nc = visCols[cPos + 1];
        }
        if (nr !== sel.r || nc !== sel.c) openCell(nr, nc, true);
      }
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [
    closePopover,
    commitEdit,
    undo,
    redo,
    applyReference,
    applyReferenceToOccurrences,
    openCell,
    nextVisibleRow,
    prevVisibleRow,
    visCols,
  ]);

  // When the selected cell is resolved (it needed work and no longer does after a
  // commit), auto-advance the cursor to the next "needs work" cell. Only commits
  // change the committed value, so this never fires mid-typing.
  useEffect(() => {
    const sel = selectedRef.current;
    if (!sel || !selWasGreenRef.current) return;
    const cell = grid[sel.r] && grid[sel.r][sel.c];
    if (cell && !cellNeedsWork(cell)) {
      selWasGreenRef.current = false;
      const target = nextGreenCell(sel.r, sel.c);
      if (target) openCell(target.r, target.c, true);
    }
  }, [grid, nextGreenCell, openCell, cellNeedsWork]);

  const FieldRow = ({ label, value }) => (
    <div className="wb-field">
      <div className="wb-field-head">
        <span className="wb-field-label">{label}</span>
        <span className="wb-field-btns">
          <button className="wb-mini" title="Copy to clipboard" onClick={() => copyText(value)}>
            Copy
          </button>
          <button className="wb-apply" onClick={() => applyValue(value)}>
            Apply
          </button>
        </span>
      </div>
      <div className="wb-field-value">{value}</div>
    </div>
  );

  return (
    <div className="App">
      <h1>L2 Localization Tool</h1>
      <div className="search-container">
        <NavBar username={username} onLogout={logout} updateDatabase={updateDatabase} />
        <center>
          <h2>Translation Workbench</h2>
        </center>

        {grid.length > 0 && (
          <div className="wb-status">
            <span>
              Progress: <strong>{progress.done}</strong> / {progress.total} rows done
              {progress.total > 0
                ? ` (${Math.round((progress.done / progress.total) * 100)}%)`
                : ""}{" "}
              · <strong>{progress.green}</strong> unfinished row{progress.green === 1 ? "" : "s"} left
            </span>
            {lastSaved && (
              <span className="wb-saved">
                Last saved {new Date(lastSaved).toLocaleTimeString()}
              </span>
            )}
          </div>
        )}

        <div className="wb-toolbar">
          <label className="wb-inline">
            Mode:&nbsp;
            <select value={mode} onChange={(e) => setMode(e.target.value)}>
              <option value="kr">KR → EN</option>
              <option value="ru">EN → RU</option>
            </select>
          </label>
          <label className="wb-inline" title="Automatically run Google Translate when opening a cell">
            <input
              type="checkbox"
              checked={autoGt}
              onChange={(e) => setAutoGt(e.target.checked)}
            />
            &nbsp;Auto-translate
          </label>
          <button
            className="wb-btn"
            onClick={clearTermCache}
            title="Flush the server's term cache so terms you edited directly in the DB are used right away"
          >
            ↻ Refresh term cache
          </button>
          <label
            className={`wb-btn${engDragOver ? " wb-dragover" : ""}`}
            onDragOver={(e) => {
              e.preventDefault();
              setEngDragOver(true);
            }}
            onDragLeave={() => setEngDragOver(false)}
            onDrop={(e) => {
              e.preventDefault();
              setEngDragOver(false);
              handleEnglishFile(e.dataTransfer.files);
            }}
          >
            {englishName ? `English: ${englishName}` : "Upload English .txt (or drop here)"}
            <input
              type="file"
              accept=".txt,text/plain"
              style={{ display: "none" }}
              onChange={(e) => handleEnglishFile(e.target.files)}
            />
          </label>
          <label
            className={`wb-btn${altDragOver ? " wb-dragover" : ""}`}
            onDragOver={(e) => {
              e.preventDefault();
              setAltDragOver(true);
            }}
            onDragLeave={() => setAltDragOver(false)}
            onDrop={(e) => {
              e.preventDefault();
              setAltDragOver(false);
              handleAltRefFile(e.dataTransfer.files);
            }}
          >
            {altRefName
              ? `${altLangLabel}: ${altRefName}`
              : `Upload ${altLangLabel} .txt (or drop here)`}
            <input
              type="file"
              accept=".txt,text/plain"
              style={{ display: "none" }}
              onChange={(e) => handleAltRefFile(e.target.files)}
            />
          </label>
          <label
            className={`wb-btn${oldDragOver ? " wb-dragover" : ""}`}
            title={`The OLD client's ${srcLangLabel} file: the preview then shows a character diff between the old and the new ${srcLangLabel} text of the selected field`}
            onDragOver={(e) => {
              e.preventDefault();
              setOldDragOver(true);
            }}
            onDragLeave={() => setOldDragOver(false)}
            onDrop={(e) => {
              e.preventDefault();
              setOldDragOver(false);
              handleOldRefFile(e.dataTransfer.files);
            }}
          >
            {oldRefName
              ? `Old ${srcLangLabel}: ${oldRefName}`
              : `Upload old ${srcLangLabel} .txt (or drop here)`}
            <input
              type="file"
              accept=".txt,text/plain"
              style={{ display: "none" }}
              onChange={(e) => handleOldRefFile(e.target.files)}
            />
          </label>
          {english && altRef && (
            <>
              <button
                className="wb-btn wb-btn-accent"
                onClick={autoFillUnchanged}
                title={
                  fillMode === "row"
                    ? `Copy the existing ${autoFillTgt} only for rows where EVERY translatable cell is unchanged from the ${autoFillSrc} reference. Applies to the rows currently shown.`
                    : `Copy the existing ${autoFillTgt} into every translatable cell whose ${autoFillSrc} text is unchanged from the ${autoFillSrc} reference. Applies to the rows currently shown.`
                }
              >
                ⇄ Auto-fill unchanged ({autoFillSrc}={autoFillSrc} → {autoFillTgt})
              </button>
              <label
                className="wb-inline"
                title="Per field: each unchanged cell fills on its own. Whole row: a row fills only if all its translatable cells are unchanged."
              >
                Match:&nbsp;
                <select value={fillMode} onChange={(e) => setFillMode(e.target.value)}>
                  <option value="field">Per field</option>
                  <option value="row">Whole row</option>
                </select>
              </label>
            </>
          )}
          {visCols.length > 0 && (
            <span
              className="wb-inline"
              title="Columns that together identify a row (e.g. skill_id + skill_level + skill_sublevel)"
            >
              Key:&nbsp;
              {visCols.map((c) => (
                <label key={c} className="wb-keycol">
                  <input
                    type="checkbox"
                    checked={keyCols.includes(c)}
                    onChange={(e) => {
                      setKeyColsManual(true);
                      setKeyCols((prev) => {
                        const next = e.target.checked
                          ? [...prev, c]
                          : prev.filter((x) => x !== c);
                        return next.length ? next.sort((a, b) => a - b) : prev;
                      });
                    }}
                  />
                  {columnKeys[c]}
                </label>
              ))}
            </span>
          )}
          <button
            className="wb-btn"
            onClick={runScan}
            disabled={!grid.length || (scan && scan.status === "loading")}
            title="Run every untranslated cell through the term database: list the strings it cannot translate (most frequent first), save translations for them, and apply the results to the grid. Applies to the rows currently shown."
          >
            🔎 Untranslated strings
          </button>
          <button className="wb-btn" onClick={copyResult} disabled={!grid.length}>
            Copy result (with styling)
          </button>
          <label className="wb-inline">
            <input
              type="checkbox"
              checked={hideUnstyled}
              onChange={(e) => setHideUnstyled(e.target.checked)}
            />
            &nbsp;Hide finished rows
          </label>
          <label
            className="wb-inline"
            title="Orange-filled rows are NEW (missing from the current files). Show only those, or only the new rows that exist in both reference files - Auto-fill unchanged then applies to the rows shown."
          >
            Show:&nbsp;
            <select value={rowFilter} onChange={(e) => setRowFilter(e.target.value)}>
              <option value="all">All rows</option>
              <option value="new">New rows only ({filterCounts.fresh})</option>
              <option value="newInRefs" disabled={!refsReady}>
                New rows in both references ({filterCounts.both})
              </option>
              <option value="newNotInRefs" disabled={!refsReady}>
                New rows missing from a reference ({filterCounts.fresh - filterCounts.both})
              </option>
              <option value="diffRef" disabled={!targetIdx.size}>
                Differs from the {targetLabel} reference ({filterCounts.diff})
              </option>
            </select>
          </label>
          {rowFilter === "diffRef" && (
            <>
              <span
                className="wb-inline"
                title="Columns compared with the target reference: a row is shown when any of them differs from it"
              >
                Compare:&nbsp;
                {textCols.map((c) => (
                  <label key={c} className="wb-keycol">
                    <input
                      type="checkbox"
                      checked={diffColsEff.includes(c)}
                      onChange={(e) => {
                        const base = diffColsEff;
                        const next = e.target.checked ? [...base, c] : base.filter((x) => x !== c);
                        setDiffCols(next.length ? next.sort((a, b) => a - b) : base);
                      }}
                    />
                    {columnKeys[c]}
                  </label>
                ))}
              </span>
              <button
                className="wb-btn wb-btn-accent"
                onClick={takeReferenceForShown}
                disabled={!visRowIdx.length}
                title={`Overwrite the compared columns of the rows shown with the ${targetLabel} reference text (one undo step)`}
              >
                ⇐ Take the {targetLabel} reference for {visRowIdx.length} row{visRowIdx.length === 1 ? "" : "s"}
              </button>
            </>
          )}
          <button className="wb-btn" onClick={() => gotoKorean(-1)} disabled={!grid.length}>
            ◀ Prev unfinished
          </button>
          <button className="wb-btn" onClick={() => gotoKorean(1)} disabled={!grid.length}>
            Next unfinished ▶
          </button>
          <button className="wb-btn" onClick={undo} disabled={!grid.length}>
            ⤺ Undo
          </button>
          <button className="wb-btn" onClick={redo} disabled={!grid.length}>
            Redo ⤼
          </button>
          <button className="wb-btn" onClick={clearAll} disabled={!grid.length}>
            Clear
          </button>
        </div>


        <textarea
          className="wb-paste"
          placeholder="Click here and paste (Ctrl+V) your Google Sheets selection — cell colors are preserved."
          onPaste={onSheetPaste}
          value=""
          onChange={() => {}}
          rows={2}
        />

        {grid.length > 0 && (
          <div className="wb-hint">
            Tip: click a column header to ignore it for highlighting. Arrow keys move
            between cells; Ctrl+E applies the {refLangLabel} reference; Ctrl+Q applies the
            reference to all duplicates; Ctrl+Z / Ctrl+Y undo/redo.
          </div>
        )}

        {grid.length > 0 && (
          <div className="wb-grid-wrap" ref={tableRef} onScroll={onGridScroll}>
            <table className="wb-grid">
              <colgroup>
                {visCols.map((c) => (
                  <col key={c} style={keyCols.includes(c) ? { width: 110 } : undefined} />
                ))}
              </colgroup>
              <thead>
                <tr>
                  {visCols.map((c) => {
                    const key = columnKeys[c];
                    const ignored = ignoredKeys.has(key);
                    return (
                      <th
                        key={c}
                        className={`wb-th${keyCols.includes(c) ? " wb-idcol" : ""}${
                          ignored ? " wb-ignored-th" : ""
                        }`}
                        onClick={() => toggleIgnore(key)}
                        title={
                          ignored
                            ? "Ignored for highlights — click to include"
                            : "Click to ignore this column for highlights"
                        }
                      >
                        {key}
                      </th>
                    );
                  })}
                </tr>
              </thead>
              <tbody>
                {(() => {
                  // Virtualized: render only the window of visible rows, with spacer
                  // rows above/below so the scrollbar still reflects the full height.
                  const total = visRowIdx.length;
                  const OVERSCAN = 6;
                  const start = Math.max(0, Math.floor(view.top / rowH) - OVERSCAN);
                  const end = Math.min(total, start + Math.ceil(view.h / rowH) + OVERSCAN * 2);
                  const topPad = start * rowH;
                  const bottomPad = Math.max(0, (total - end) * rowH);
                  const colCount = visCols.length;
                  const out = [];
                  if (topPad > 0) {
                    out.push(
                      <tr key="__top" aria-hidden="true">
                        <td colSpan={colCount} style={{ height: topPad, padding: 0, border: 0 }} />
                      </tr>
                    );
                  }
                  for (let i = start; i < end; i++) {
                    const r = visRowIdx[i];
                    const row = grid[r];
                    out.push(
                      <tr key={r} ref={i === start ? measureRow : undefined}>
                        {visCols.map((c) => {
                          const cell = row[c];
                          const isSel = selected && selected.r === r && selected.c === c;
                          const val = isSel ? editValue : cell.value;
                          const bg = effectiveBg(cell, val);
                          const warned = warnedCells.has(`${r},${c}`);
                          const style =
                            bg || warned
                              ? {
                                  backgroundColor: bg || undefined,
                                  color: warned ? "#0b46c2" : bg ? "#111" : undefined,
                                  fontWeight: warned ? 700 : undefined,
                                }
                              : undefined;
                          return (
                            <td
                              key={c}
                              data-r={r}
                              data-c={c}
                              className={`wb-cell${keyCols.includes(c) ? " wb-idcol" : ""}${
                                isSel ? " wb-selected" : ""
                              }`}
                              style={style}
                              onClick={() => openCell(r, c, false)}
                              title={
                                warned
                                  ? `Differs from the ${refLangLabel} reference (lines / numbers / font tags)`
                                  : "Click to inspect / edit"
                              }
                            >
                              <div className="wb-cell-inner">{val}</div>
                            </td>
                          );
                        })}
                      </tr>
                    );
                  }
                  if (bottomPad > 0) {
                    out.push(
                      <tr key="__bottom" aria-hidden="true">
                        <td colSpan={colCount} style={{ height: bottomPad, padding: 0, border: 0 }} />
                      </tr>
                    );
                  }
                  return out;
                })()}
              </tbody>
            </table>
          </div>
        )}

        {selected && popover && (
          <div className={`wb-popover${showPreview ? " wb-popover-wide" : ""}`}>
            <div className="wb-pop-main">
            <div className="wb-pop-header">
              <span>
                {selKey ? selKey : `Col ${selected.c + 1}`}
                {rowId ? ` · ID ${rowId}` : ""}
                {occurrences > 1 ? ` · ${occurrences} occurrences` : ""}
              </span>
              <span className="wb-pop-head-btns">
                <button
                  className={`wb-mini${showPreview ? " wb-mini-on" : ""}`}
                  onClick={() => setShowPreview((v) => !v)}
                  title="Toggle a game-like preview of the cell and its reference, shown to the right"
                >
                  {showPreview ? "Preview ◂" : "Preview ▸"}
                </button>
                <button className="wb-x" onClick={closePopover}>
                  ×
                </button>
              </span>
            </div>

            {warnings.length > 0 && (
              <div className="wb-warn" title="Selected cell vs reference — mismatches">
                <span className="wb-warn-icon">⚠</span>
                {warnings.map((wn) => (
                  <span key={wn.kind} className="wb-warn-chip">
                    {wn.text}
                  </span>
                ))}
              </div>
            )}

            <div className="wb-pop-section wb-pop-section-row">
              <span>Edit cell</span>
              <span className="wb-editor-switch">
                <button
                  className={`wb-mini${editorKind === "monaco" ? " wb-mini-on" : ""}`}
                  onClick={() => setEditorKind("monaco")}
                  title="VS Code-like editor: line breaks shown as real lines, syntax colours, line numbers, multi-cursor. Right-click a selection to look up a name."
                >
                  Code editor
                </button>
                <button
                  className={`wb-mini${editorKind === "plain" ? " wb-mini-on" : ""}`}
                  onClick={() => setEditorKind("plain")}
                  title="The plain text box: the raw cell text, with line breaks written as \n"
                >
                  Plain box
                </button>
              </span>
            </div>
            {editorKind === "monaco" ? (
              <div className="wb-monaco" ref={monacoBoxRef} title="Line breaks are shown as real lines and saved back as \n. Ctrl+S saves.">
                <Editor
                  height="100%"
                  theme="vs-dark"
                  language="l2dat"
                  defaultValue={toDisplayText(editValue)}
                  onMount={onMonacoMount}
                  onChange={onMonacoChange}
                  loading={<div className="wb-muted">Loading editor…</div>}
                  options={{
                    wordWrap: "on",
                    minimap: { enabled: false },
                    fontSize: 13,
                    lineNumbers: "on",
                    lineNumbersMinChars: 2,
                    lineDecorationsWidth: 6,
                    glyphMargin: false,
                    folding: false,
                    scrollBeyondLastLine: false,
                    automaticLayout: true,
                    fixedOverflowWidgets: true,
                    // No autocomplete in a translation cell: an accepted suggestion
                    // must never rewrite text behind the translator's back.
                    quickSuggestions: false,
                    wordBasedSuggestions: false,
                    suggestOnTriggerCharacters: false,
                    unicodeHighlight: { ambiguousCharacters: false, invisibleCharacters: true },
                  }}
                />
              </div>
            ) : (
              <textarea
                className="wb-edit"
                value={editValue}
                onChange={(e) => setEditValue(e.target.value)}
                onContextMenu={onEditContextMenu}
                title="Select text and right-click to look up a name"
                rows={4}
              />
            )}
            <div className="wb-pop-actions">
              <button className="wb-btn" onClick={commitEdit}>
                Save
              </button>
              {occurrences > 1 && (
                <button
                  className="wb-btn"
                  onClick={() => replaceOccurrences(false)}
                  title={`Write this text into every cell pasted with the same ${selKey || "text"} (${occurrenceInfo.total} cells)${
                    occurrenceInfo.skipped
                      ? `. ${occurrenceInfo.skipped} edited separately are left alone.`
                      : "."
                  }`}
                >
                  Apply this to all {occurrenceInfo.count}
                </button>
              )}
              {occurrences > 1 && occurrenceInfo.skipped > 0 && (
                <button
                  className="wb-btn"
                  onClick={() => replaceOccurrences(true)}
                  title="Also overwrite the cells of this group that were edited separately"
                >
                  + {occurrenceInfo.skipped} edited
                </button>
              )}
              {occurrences > 1 && refValue != null && (
                <button
                  className="wb-btn wb-btn-accent"
                  onClick={applyReferenceToOccurrences}
                  title="Apply the reference string to the whole group (Ctrl+Q)"
                >
                  ★ Apply reference to all {occurrenceInfo.count} (Ctrl+Q)
                </button>
              )}
            </div>

            <div className="wb-pop-section">
              Google Translate ({mode === "ru" ? "EN→RU" : "KR→EN"})
              <button
                className="wb-mini"
                onClick={() => fetchGT(editValueRef.current)}
                disabled={gt.status === "loading"}
              >
                {gt.status === "loading" ? "…" : "Translate"}
              </button>
            </div>
            <div className="wb-pop-scroll">
              {gt.status === "loading" && <div className="wb-muted">Translating&hellip;</div>}
              {gt.status === "done" && <FieldRow label="Translation" value={gt.text} />}
              {gt.status === "idle" && (
                <div className="wb-muted">Auto-translate is off — click Translate.</div>
              )}
              {gt.status === "none" && (
                <div className="wb-muted">No Korean text in this cell.</div>
              )}
              {(gt.status === "offline" || gt.status === "error") && (
                <div className="wb-muted">
                  {gt.status === "offline" ? "Server offline" : "Translation failed"} &mdash;{" "}
                  <a href={gtLink(editValue, mode)} target="_blank" rel="noreferrer">
                    open in Google Translate
                  </a>
                </div>
              )}
            </div>

            <div className="wb-pop-section">
              Term memory
              <button
                className="wb-mini"
                onClick={runTermMemory}
                disabled={tm.status === "loading"}
              >
                {tm.status === "loading" ? "…" : "Suggest"}
              </button>
            </div>
            {tm.status === "done" &&
              (() => {
                const segs = diffTokens(tm.before, tm.after);
                return (
                  <div className="wb-pop-scroll">
                    <div className="wb-diff-label">Before</div>
                    <div
                      className="wb-diff l2"
                      dangerouslySetInnerHTML={{ __html: prettyDiff(segs, "before") }}
                    />
                    <div className="wb-diff-label">After</div>
                    <div
                      className="wb-diff l2"
                      dangerouslySetInnerHTML={{ __html: prettyDiff(segs, "after") }}
                    />
                    <span className="wb-field-btns">
                      <button className="wb-mini" title="Copy to clipboard" onClick={() => copyText(tm.after)}>
                        Copy
                      </button>
                      <button className="wb-apply" onClick={applyTerm}>
                        Apply
                      </button>
                    </span>
                  </div>
                );
              })()}
            {tm.status === "error" && (
              <div className="wb-muted">Term lookup failed{tm.message ? ` - ${tm.message}` : "."}</div>
            )}

            <div className="wb-pop-section">
              {refLangLabel} reference{refFile ? ` (${refFile.type})` : ""}
              {selKey ? ` · ${selKey}` : ""}
            </div>
            <div className="wb-pop-scroll">
              {!refFile ? (
                <div className="wb-muted">Upload the {refLangLabel} .txt to see references.</div>
              ) : !record ? (
                <div className="wb-muted">
                  No {refLangLabel} match for ID &ldquo;{rowId || "?"}&rdquo;.
                </div>
              ) : refValue !== null ? (
                <>
                  <FieldRow label={selKey} value={refValue} />
                  <div className="wb-muted wb-hotkey">Ctrl+E applies this</div>
                </>
              ) : (
                <div className="wb-muted">
                  No &ldquo;{selKey}&rdquo; field in the reference record.
                </div>
              )}
              {record && record.fields && Object.keys(record.fields).length > 0 && (
                <details className="wb-allfields">
                  <summary>All fields ({Object.keys(record.fields).length})</summary>
                  {Object.entries(record.fields).map(([k, v]) => (
                    <FieldRow key={k} label={k} value={v} />
                  ))}
                </details>
              )}
            </div>
            </div>
            {showPreview && (
              <div className="wb-pop-preview">
                <div className="wb-pv-title">Preview</div>
                <div className="wb-diff-label">Cell</div>
                <div
                  className={`wb-pv l2${hasCyrillic(editValue) ? " ru" : ""}`}
                  dangerouslySetInnerHTML={{ __html: cellPreviewHtml }}
                />
                <div className="wb-diff-label">
                  {refLangLabel} reference{selKey ? ` · ${selKey}` : ""}
                </div>
                {refValue != null ? (
                  <div
                    className={`wb-pv l2${hasCyrillic(refValue) ? " ru" : ""}`}
                    dangerouslySetInnerHTML={{ __html: refPreviewHtml }}
                  />
                ) : (
                  <div className="wb-muted">
                    {!refFile
                      ? `Upload the ${refLangLabel} .txt to see the reference.`
                      : "No reference for this cell."}
                  </div>
                )}
                {warnings.length > 0 && (badLines.cell.size > 0 || badLines.ref.size > 0) && (
                  <div className="wb-pv-legend">
                    <span className="wb-pv-swatch" />
                    lines with no counterpart on the other side ({warnings.map((w) => w.text).join(", ")})
                  </div>
                )}
                <div className="wb-diff-label wb-pv-diff-label">
                  Old client → new client ({srcLangLabel}
                  {selKey ? ` · ${selKey}` : ""})
                </div>
                {!oldRef ? (
                  <div className="wb-muted">
                    Upload the old {srcLangLabel} .txt to see what changed between the clients.
                  </div>
                ) : oldSrc == null ? (
                  <div className="wb-muted">
                    Not in the old client{rowId ? ` (ID ${rowId})` : ""}: this {selKey || "field"} is new.
                  </div>
                ) : clientDiff && clientDiff.same ? (
                  <div className="wb-muted">Unchanged since the old client.</div>
                ) : clientDiff ? (
                  <>
                    <div className="wb-diff-label">Old</div>
                    <div
                      className={`wb-pv wb-cdiff l2${hasCyrillic(oldSrc) ? " ru" : ""}`}
                      dangerouslySetInnerHTML={{ __html: clientDiff.beforeHtml }}
                    />
                    <div className="wb-diff-label">
                      New{newSrc == null ? " (this cell's current text)" : ""}
                    </div>
                    <div
                      className={`wb-pv wb-cdiff l2${hasCyrillic(newSrc == null ? editValue : newSrc) ? " ru" : ""}`}
                      dangerouslySetInnerHTML={{ __html: clientDiff.afterHtml }}
                    />
                    {newSrc == null && (
                      <div className="wb-muted">
                        Upload the new {srcLangLabel} .txt to compare the untouched source instead
                        of this cell's current text.
                      </div>
                    )}
                  </>
                ) : null}
              </div>
            )}
          </div>
        )}

        {/* Find & replace: floating panel on the left edge */}
        <div className="wb-fr">
          {frOpen ? (
            <div className="wb-fr-card">
              <div className="wb-fr-head">
                <b>Find &amp; replace</b>
                <button className="wb-x" onClick={() => setFrOpen(false)} title="Collapse">
                  ×
                </button>
              </div>
              <input
                className="wb-input"
                placeholder={useRegex ? "Find (regular expression)…" : "Find…"}
                value={find}
                onChange={(e) => setFind(e.target.value)}
                spellCheck={false}
              />
              <input
                className="wb-input"
                placeholder={useRegex ? "Replace with… ($1, $& allowed)" : "Replace with…"}
                value={replace}
                onChange={(e) => setReplace(e.target.value)}
                spellCheck={false}
              />
              <div className="wb-fr-opts">
                <label>
                  <input
                    type="checkbox"
                    checked={matchCase}
                    onChange={(e) => setMatchCase(e.target.checked)}
                  />
                  &nbsp;Match case
                </label>
                <label title="Treat Find as a JavaScript regular expression. The replacement may use $1…$99, $& (the whole match) and $<name>.">
                  <input
                    type="checkbox"
                    checked={useRegex}
                    onChange={(e) => setUseRegex(e.target.checked)}
                  />
                  &nbsp;Regex
                </label>
              </div>
              {finder.error && <div className="wb-fr-err">{finder.error}</div>}
              <div className="wb-fr-actions">
                <button
                  className="wb-btn"
                  onClick={previewReplace}
                  disabled={!finder.re || !grid.length}
                  title="List every match and what it would become. Nothing is changed until you confirm."
                >
                  Preview…
                </button>
                <button
                  className="wb-btn wb-btn-accent"
                  onClick={replaceAll}
                  disabled={!finder.re || !grid.length}
                >
                  Replace all
                </button>
              </div>
              <div className="wb-fr-hint">
                Runs on the raw cell text of the whole grid, where a line break is the two characters{" "}
                <code>\n</code>
                {useRegex ? (
                  <>
                    {" "}
                    (in a regex write it as <code>\\n</code>)
                  </>
                ) : null}
                .
              </div>
            </div>
          ) : (
            <button
              className="wb-fr-tab"
              onClick={() => setFrOpen(true)}
              title="Find & replace across the grid (plain text or regex, with a preview)"
            >
              🔍 Find &amp; replace
            </button>
          )}
        </div>

        {frPreview && (
          <div
            className="wb-scan-backdrop"
            onMouseDown={(e) => {
              if (e.target === e.currentTarget) setFrPreview(null);
            }}
          >
            <div className="wb-scan">
              <div className="wb-scan-head">
                <b>Replace preview</b>
                <span className="wb-scan-meta">
                  {frPreview.matches} match{frPreview.matches === 1 ? "" : "es"} in {frPreview.cells.length}{" "}
                  cell{frPreview.cells.length === 1 ? "" : "s"}
                  {frPreview.cells.length > 300 ? " - the first 300 cells are listed" : ""}
                </span>
                <button className="wb-x" onClick={() => setFrPreview(null)}>
                  ×
                </button>
              </div>
              <div className="wb-scan-actions">
                <button className="wb-btn wb-btn-accent" onClick={replaceAll}>
                  Replace all {frPreview.matches}
                </button>
                <button className="wb-btn" onClick={() => setFrPreview(null)}>
                  Cancel
                </button>
                <span className="wb-scan-meta">Nothing has been changed yet.</span>
              </div>
              <div className="wb-fr-list">
                {frPreview.cells.slice(0, 300).map((x) => {
                  const seg = replaceSegments(x.before, x.hits);
                  const id = rowLookupKey(x.r);
                  return (
                    <div className="wb-fr-item" key={`${x.r},${x.c}`}>
                      <div className="wb-fr-where">
                        <button
                          className="wb-mini"
                          onClick={() => {
                            setFrPreview(null);
                            openCell(x.r, x.c, true);
                          }}
                          title="Close the preview and open this cell"
                        >
                          Row {x.r + 1}
                        </button>
                        {id ? ` · ID ${id}` : ""}
                        {columnKeys[x.c] ? ` · ${columnKeys[x.c]}` : ""} · {x.hits.length} match
                        {x.hits.length === 1 ? "" : "es"}
                      </div>
                      <div className="wb-fr-text">
                        {seg.before.map((s, i) =>
                          s.hit ? (
                            <span className="wb-del" key={i}>
                              {s.t}
                            </span>
                          ) : (
                            <span key={i}>{s.t}</span>
                          )
                        )}
                      </div>
                      <div className="wb-fr-text">
                        {seg.after.map((s, i) =>
                          s.hit ? (
                            <span className="wb-add" key={i}>
                              {s.t}
                            </span>
                          ) : (
                            <span key={i}>{s.t}</span>
                          )
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
        )}

        {scan && (
          <div
            className="wb-scan-backdrop"
            onMouseDown={(e) => {
              if (e.target === e.currentTarget && scan.status !== "loading") setScan(null);
            }}
          >
            <div className="wb-scan">
              <div className="wb-scan-head">
                <b>Untranslated strings</b>
                {scan.status === "done" && (
                  <span className="wb-scan-meta">
                    {scan.cells.length} untranslated cell{scan.cells.length === 1 ? "" : "s"} scanned
                    {scan.scoped ? " (rows shown only)" : ""}
                  </span>
                )}
                <button
                  className="wb-x"
                  onClick={() => {
                    scanReq.current += 1; // abandon a running scan
                    setScan(null);
                  }}
                >
                  ×
                </button>
              </div>
              {scan.status === "loading" ? (
                <div className="wb-scan-progress">
                  <div className="wb-scan-bar">
                    <div
                      className="wb-scan-fill"
                      style={{ width: `${scan.total ? Math.round((scan.done / scan.total) * 100) : 0}%` }}
                    />
                  </div>
                  <span>
                    Scanning… {scan.done} / {scan.total} cells
                  </span>
                </div>
              ) : (
                (() => {
                  const changed = scan.cells.filter((x) => x.out !== x.src);
                  const full = changed.filter((x) => x.ok).length;
                  return (
                    <>
                      <div className="wb-scan-actions">
                        <button
                          className="wb-btn wb-btn-accent"
                          onClick={applyScan}
                          disabled={!changed.length}
                          title="Writes the translated text into every cell the term database could translate, fully or line by line. One undo step."
                        >
                          ⇄ Apply term translations to {changed.length} cell{changed.length === 1 ? "" : "s"}
                        </button>
                        <span className="wb-scan-meta">
                          {changed.length
                            ? `${full} fully translated, ${changed.length - full} partly (some lines)`
                            : scan.applied
                            ? `Applied to ${scan.applied} cell${scan.applied === 1 ? "" : "s"} - re-scan after saving more terms`
                            : "The term database cannot translate any of these cells yet"}
                        </span>
                      </div>
                      {scan.missing.length === 0 ? (
                        <div className="wb-muted">
                          Every line of these cells has a term. Apply the translations above.
                        </div>
                      ) : (
                        <MissingTerms
                          missing={scan.missing}
                          resetKey={scan.stamp}
                          notify={wbToast}
                          onRerun={runScan}
                          rerunLabel="Re-scan"
                          rerunTitle="Scan again so the terms you just saved are used"
                          onPick={(m) => {
                            setFind(m.sample || m.key);
                            setUseRegex(false);
                            setFrOpen(true);
                            setScan(null);
                            wbToast("info", "Find & replace panel filled - type the replacement, then Preview or Replace all.");
                          }}
                          pickLabel="Find"
                          pickTitle="Put this line into the Find box for a plain find & replace instead of a term"
                        />
                      )}
                    </>
                  );
                })()
              )}
            </div>
          </div>
        )}

        {ctxMenu && (
          <div className="wb-ctx" style={{ top: ctxMenu.y, left: ctxMenu.x }}>
            <div className="wb-ctx-head">
              Name lookup: <strong>{ctxMenu.searchStr}</strong>
            </div>
            {ctxMenu.loading ? (
              <div className="wb-ctx-muted">Searching&hellip;</div>
            ) : ctxMenu.results.length === 0 ? (
              <div className="wb-ctx-muted">No matches.</div>
            ) : (
              <div className="wb-ctx-list">
                {ctxMenu.results.map((r, i) => {
                  const en = r.outputLanguage && r.outputLanguage.name;
                  return (
                    <button
                      key={i}
                      className="wb-ctx-item"
                      disabled={!en}
                      onClick={() => replaceSelection(en)}
                      title={en ? `Replace with "${en}"` : "No English name"}
                    >
                      <span
                        className={`result-type-badge type-${(r.resultType || "").toLowerCase()}`}
                      >
                        {r.resultType}
                      </span>
                      <span className="wb-ctx-id">{r.id}</span>
                      <span className="wb-ctx-name">{en || "—"}</span>
                      <span className="wb-ctx-src">{r.inputLanguage && r.inputLanguage.name}</span>
                    </button>
                  );
                })}
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default Workbench;
